import { defineStore } from 'pinia';
import { getAudioInformation } from '@/services/AudioService';
import { deleteShow, duplicateShow } from '@/services/ShowsService';
import { getShowStats } from '@/services/ShowsService';
import { createUniqueID } from '@/helpers/createID';
import { useWaveformStore } from './waveform';
import {
	ADD_EVENT_BUTTON_STATES,
	EVENT_EDIT_METHODS,
	SHOW_TYPES,
	SHOW_METHODS,
	PRODUCTS,
	SHOW_ORDER_STATUES,
	EVENT_TYPES,
	COLUMN_KEYS,
	column_key_for_index,
	event_object_keys_for_column_key,
	alternate_column_for_main_column_key,
	column_index_for_key,
	DEFAULT_COLUMN_KEY_FOR_INDEX,
	DEFAULT_COLUMN_INDEX_FOR_KEY,
	SAVE_TRIGGERED_FROM,
	MAX_NUMBER_OF_FREE_TIER_EVENTS,
	METHOD_TO_EDIT_TIME,
} from '@/constants';
import { shortenTime } from '@/helpers/shortenTime';
import { toSeconds } from '@/helpers/toSeconds';
import { convertFormattedStringIntoSeconds, deTransformScriptArray, splitFormatedStringIntoTimeElements, getSafeString, toSmallestDisplayTime, formatDisplayableTime, sha256HashOfObject } from '@/helpers/commonFunctions';
import {
	insertTimedEventBelow,
	validateShow,
	validateEventElement,
	insertTimedEventAbove,
	shiftEvents,
	returnIndexOfEventIds,
	changeEventValueInArray,
	calculateEventTimeWithMethod,
	addNewEventToTable,
	validateEvents,
	removeErrorsAndWarningsFromEvent,
	calculateEventTimeForEvent,
	validateEvent,
	sortEvents,
	updateShowType,
	synchronizeEventTimesAndUpdateFlags,
	createStepParentChildRelationship,
	updateOffsetsAndRelativeTime,
	calculateEventTimes,
	canEventTimeBeUpdated,
	updateRelativeAndCalculatedTimesForAEvent,
	getPreviousEventDetails,
	updateCuesUsedPerChannel,
	getNextCueForChannel,
	removeUsedCueFromChannel,
	removeUsedChannelFromUsedCues,
	syncSnappingOfStepEventsAfterPreviousEventIsEnded,
} from '@/helpers/eventsHelper';
import { saveShow } from '../services/ShowsService';
import { getShow } from '@/services/ShowsService';
import { deleteAudio } from '@/services/AudioService';
import { generateEventHighlightingTimeMarkers, resetLocalStates } from '@/helpers/eventHighlight';
import { useSettingStore } from '@/stores/setting';
import { useInventoryStore } from './inventory';
import { useAuthStore } from '@/stores/auth';
import Modal from '@/assets/libraries/ModalHandler';
import { NotificationCenter, NotificationHandler, NotificationType } from '@/assets/libraries/NotificationHandler';
import * as InventoryService from '@/services/InventoryService';
import { sortSelectedEventsByRowIndex } from '@/helpers/commonFunctions';
import { clearSelectedRowsBackgroundColor, addSelectedRowBackgroundColor, clearEventBackgroundColor, addSelectedRowBackgroundColorToAllEvents, shouldHidePulseTimeColumn, shouldHidePrefireColumn, shouldHideLiftTimeColumn, shouldHideDisableFiringGroupColumn, shouldHideQuantityColumn } from '@/helpers/eventsTableRowHelper';
import { syncEventToMyFirework, isMyFireworkOutOfSync } from '@/helpers/myFireworkSync';
import { useUndoManagerStore } from './undoManager';
import { saveRevision } from '@/services/RevisionHistory';
import setupI18n from '@/i18n';
import { OfflineModeShowManager, OfflineModeEditsPendingSyncManager, OfflineAudioManager, _getRow } from '@/services/OfflineMode';
import { VERSION_NUMBER, BUILD_NUMBER } from '@/VersionInfo.js';
import { useFireworkStore } from './firework';
import { useLoaderStore } from '@/stores/loader';
import { useLayoutStore } from './layout';
import { getManyFireworkData } from '@/services/FireworksService';

const i18n = setupI18n();
const { t } = i18n.global;

// Column tokens used by the multi-cell edit feature
export const MULTI_EDIT_COLUMNS = Object.freeze({
	UNIVERSE: 'UniverseCell',
	CHANNEL: 'ChannelCell',
	CUE: 'CueCell',
	DESCRIPTION: 'EventDescriptionCell',
	DURATION: 'DurationCell',
	PRE_FIRE: 'PreFireCell',
	LIFT_TIME: 'LiftTimeCell',
	PULSE: 'PulseTimeCell',
	DEFINED_POSITION: 'DefinedPositionCell',
	DISABLE_FIRING_GROUP: 'DisableFiringGroupCell',
	CUSTOM: 'Custom',
});

export const MULTI_EDIT_CASCADING_COLUMNS = new Set([MULTI_EDIT_COLUMNS.DURATION]);

// Defined position and disable firing group are labels on an event - they never move it in time,
// so the waveform does not need to be rebuilt when they change.
export const MULTI_EDIT_NON_WAVEFORM_COLUMNS = new Set([MULTI_EDIT_COLUMNS.DEFINED_POSITION, MULTI_EDIT_COLUMNS.DISABLE_FIRING_GROUP]);

export function parseMultiEditColumnCell(column_cell) {
	if (typeof column_cell === 'string' && column_cell.startsWith(`${MULTI_EDIT_COLUMNS.CUSTOM}:`)) {
		return { base_column: MULTI_EDIT_COLUMNS.CUSTOM, custom_column: column_cell.slice(MULTI_EDIT_COLUMNS.CUSTOM.length + 1) };
	}
	return { base_column: column_cell, custom_column: undefined };
}

export function doesMultiEditColumnApply(base_column, event) {
	if (event == undefined || event.isCOMMENT === true) {
		return false;
	}
	if (base_column === MULTI_EDIT_COLUMNS.UNIVERSE) {
		const is_disabled = event.isSTEP === false && (event.isDMX === false || event.isDMX == undefined) && event.isCOMMENT == undefined;
		return is_disabled === false;
	}
	return true;
}
// Content-only fields used for save-change detection — mirrors BE hashShowData in shows.service.js.
// If you add a new content field here, also add it to BE hashShowData. Metadata fields (e.g. last_known_updated) must NOT be in this list.
export const SHOW_CONTENT_HASH_FIELDS = [
	'show_ID',
	'firmware_version',
	'uses_audio',
	// 'audio_file_id', // EVS-1058: audio_file_id is set exclusively via the dedicated audio endpoint; including it here allows stale sessions to overwrite the server's correct value
	'audio_box_filename',
	'script_name',
	'custom_name',
	'custom_columns',
	'script_atributes',
	'cues',
	'estimated_show_duration',
	'disable_firing_groups',
	'defined_positions',
];

function buildShowHashPayload(show_data) {
	const payload = {};
	for (const field of SHOW_CONTENT_HASH_FIELDS) {
		payload[field] = show_data[field];
	}
	// Normalize undefined/null to [] so the hash is consistent regardless of whether the field was ever set
	payload.defined_positions = show_data.defined_positions || [];
	return payload;
}

export const useShowStore = defineStore('show', {
	state: () => ({
		active_show: null,
		active_show_stats: null,
		active_show_total_columns: 0,
		active_show_selected_events: [],
		active_show_selected_fireworks: [],
		copied_events: [],
		info_about_loaded_song: null,
		is_event_time_editor_open: false,
		event_edit_modal_position: undefined,
		event_edit_attributes: {},
		breakout_modal_state: undefined, // { event_id, top, left } when open
		set_my_firework_modal_state: undefined, // { event_id, top, left } when open
		active_show_selected_column_cell: null,
		currently_dragged_firework: null,
		currently_dragged_dmx: null,
		placeholder_row_object: {
			show_active_show_placeholder_row: false,
			active_show_placeholder_position: null,
			placeholder_row_event_id: null,
			next_or_previous_cue_id: null,
		},
		next_step_event_index: undefined,
		previous_step_event_index: undefined,
		// consective_pause_play: false,
		event_highlighting_time_markers: new Map(),
		// paused_at_step: false,
		a_change_has_been_made: false,
		edits_since_last_save: 0, // bumped on every setChangeMadeToTrue() call; lets saveShow() tell whether the user edited again while a save was in flight
		show_saved: false,
		show_saving_status: {
			is_saving: false,
			previous_saved_locally_at: undefined,
			previous_saved_to_cloud_at: undefined,
			latest_saved_locally_at: undefined,
			latest_saved_to_cloud_at: undefined,
			error_saving: undefined,
		},
		cell_error: undefined,
		highlight_column: {
			event_id: null,
			column_indexes: [],
			is_custom_column: false,
		},
		insert_dragged_event_at_index: undefined,
		active_show_order: undefined,
		// below attributes are used to update waveform without reset
		toggle_waveform_reset: false,
		newly_updated_selected_events: null,
		action_being_performed: null,
		scroll_to_event_id: null,
		is_show_locked: false,
		no_audio_show: false,

		disable_group_modal_position: undefined,
		save_show_interval: undefined,
		user_is_editing: false,

		context_menu_opened_on_focused_cell_id: undefined,
		current_selected_row_index_for_pasting: undefined,
		should_highlight_cell_text: true,
		context_menu_opened_at_range: undefined,

		step_events: [],
		trigger_find_and_replace_search: false,
		found_items_to_be_highlighted: [],

		is_case_sensitivity_enabled_in_find_replace: false,
		is_my_shows_open: false,
		is_create_new_show_open: false,
		is_import_show_open: false,
		enable_auto_save: true,

		do_not_seek_audio_playback: false,
		new_event_selection_timeout: null,

		dmx_value_editor_is_open_for_event_id: undefined,

		defined_position_modal_position: undefined,
		force_update_show_details: undefined,
		resolveForceUpdate: undefined,

		last_save_show_hash: undefined,
	}),
	getters: {
		// Returns a map of event_id -> { top, bottom } for the cells that will be updated by the current edit, so cells can draw an indigo outline.
		multi_edit_border_map() {
			const empty = {};
			if (this.user_is_editing !== true) return empty;

			const column_cell = this.active_show_selected_column_cell;
			if (column_cell == undefined) return empty;

			const selected_ids = this.active_show_selected_events || [];
			if (selected_ids.length <= 1) return empty;

			const events = this.active_show?.events || [];
			if (events.length === 0) return empty;

			const { base_column } = parseMultiEditColumnCell(column_cell);
			const selected_set = new Set(selected_ids);

			const is_target = new Array(events.length);
			for (let i = 0; i < events.length; i++) {
				is_target[i] = selected_set.has(events[i].event_id) && doesMultiEditColumnApply(base_column, events[i]);
			}

			const border_map = {};
			for (let i = 0; i < events.length; i++) {
				if (is_target[i] !== true) continue;
				border_map[events[i].event_id] = {
					top: i === 0 || is_target[i - 1] !== true,
					bottom: i === events.length - 1 || is_target[i + 1] !== true,
				};
			}
			return border_map;
		},
		filtered_events_based_on_show_type(state) {
			const active_show = state.active_show;
			if (!active_show) return [];
			const firmware_version = active_show.firmware_version;
			const show_type = active_show.show_attributes?.show_type;
			const events = active_show.events || [];
			const hide_dmx_rows = firmware_version < 6 || show_type === SHOW_TYPES.PYRO.key;
			const hide_pyro_rows = firmware_version >= 6 && show_type === SHOW_TYPES.DMX.key;
			if (hide_dmx_rows) {
				return events.filter((event) => !event.isDMX);
			}
			if (hide_pyro_rows) {
				return events.filter((event) => event.isDMX);
			}
			return events;
		},
	},
	actions: {
		openEventEditModal(modal_position) {
			if (this.is_show_locked == true) {
				return;
			}

			this.event_edit_attributes = {};
			this.is_event_time_editor_open = true;
			this.event_edit_modal_position = modal_position;

			// set default edit time values to equal event time
			const split_event_time = splitFormatedStringIntoTimeElements(modal_position.event_time);
			this.setNewTime(split_event_time.min, modal_position.event_id, split_event_time, 'minute');
			this.setNewTime(split_event_time.sec, modal_position.event_id, split_event_time, 'second');
			this.setNewTime(split_event_time.milSec, modal_position.event_id, split_event_time, 'milli_sec');

			if (this.dmx_value_editor_is_open_for_event_id != undefined) {
				this.closeDMXValueEditor();
			}
		},
		openDisableGroupModal(modal_position) {
			if (this.is_show_locked == true) {
				return;
			}

			this.defined_position_modal_position = undefined;
			this.disable_group_modal_position = modal_position;
		},
		openDefinedPositionModal(modal_position) {
			if (this.is_show_locked == true) {
				return;
			}

			this.disable_group_modal_position = undefined;
			this.defined_position_modal_position = modal_position;
		},
		closeEventEditModal() {
			this.event_edit_attributes = {};
			this.is_event_time_editor_open = false;
			this.event_edit_modal_position = undefined;
		},

		openBreakoutModal({ event_ids, top, left }) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			this.breakout_modal_state = { event_ids: ids, top, left };
		},
		closeBreakoutModal() {
			this.breakout_modal_state = undefined;
		},
		openSetMyFireworkModal({ event_ids, top, left }) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			this.set_my_firework_modal_state = { event_ids: ids, top, left };
		},
		closeSetMyFireworkModal() {
			this.set_my_firework_modal_state = undefined;
		},
		clearMyFireworkForEvents(event_ids) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			const events_by_id = new Map((this.active_show?.events || []).map((e) => [e.event_id, e]));
			let changed = false;
			for (const id of ids) {
				const event = events_by_id.get(id);
				if (event == undefined || event.my_firework_id == undefined) continue;
				event.firework_name = undefined;
				event.duration = undefined;
				event.duration_in_seconds = undefined;
				event.lift_time = undefined;
				event.lift_time_in_seconds = undefined;
				event.my_firework_id = undefined;
				changed = true;
			}
			if (changed) {
				this.setChangeMadeToTrue();
				useInventoryStore()?.setTriggerFindInventoryChanges();
			}
		},
		setMyFireworkForEvents(event_ids, firework) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			const original_selection = [...this.active_show_selected_events];
			this.active_show_selected_events = ids;
			this.insertFireworkIntoRow(firework);
			this.active_show_selected_events = original_selection;
		},
		// Returns the subset of `event_ids` (single id or array) whose events can host breakout fireworks
		// (i.e. exist in the active show and are not Comment or DMX). Single source of truth used by the
		// context menu, keyboard shortcut, and breakout modal.
		getEligibleBreakoutEventIds(event_ids) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			const events = this.active_show?.events || [];
			const events_by_id = new Map(events.map((event) => [event.event_id, event]));
			return ids.filter((id) => {
				const event = events_by_id.get(id);
				return event != undefined && event.isCOMMENT != true && event.isDMX != true;
			});
		},
		addBreakoutFirework(event_ids, firework) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			let change_made = false;
			for (const event_id of ids) {
				const event = this.active_show?.events?.find((event) => event.event_id == event_id);
				if (event == undefined) continue;
				if (event.isCOMMENT == true || event.isDMX == true) continue;
				if (Array.isArray(event.breakout_fireworks) == false) {
					event.breakout_fireworks = [];
				}

				// Dedup: if this firework is already a breakout on this event, just bump its quantity
				if (firework.firework_ID != undefined) {
					const existing = event.breakout_fireworks.find((breakout) => breakout.my_firework_id != undefined && breakout.my_firework_id == firework.firework_ID);
					if (existing != undefined) {
						existing.quantity = (Number(existing.quantity) || 0) + 1;
						change_made = true;
						continue;
					}
				}

				event.breakout_fireworks.push({
					quantity: 1,
					firework_name: firework.name,
					duration: firework.duration != undefined ? shortenTime(firework.duration) : undefined,
					duration_in_seconds: firework.duration != undefined ? toSeconds(firework.duration) : undefined,
					lift_time: firework.lift_time != undefined ? shortenTime(firework.lift_time) : undefined,
					lift_time_in_seconds: firework.lift_time != undefined ? toSeconds(firework.lift_time) : undefined,
					igniter_pre_fire: undefined,
					igniter_pre_fire_in_seconds: undefined,
					pulse: undefined,
					my_firework_id: firework.firework_ID,
					inventory_item_id: firework.inventory_item_id,
				});
				change_made = true;
			}
			if (change_made) this.setChangeMadeToTrue();
		},
		removeBreakoutFirework(event_ids, { my_firework_id, index } = {}) {
			const ids = Array.isArray(event_ids) ? event_ids : [event_ids];
			let change_made = false;
			for (const event_id of ids) {
				const event = this.active_show?.events?.find((event) => event.event_id == event_id);
				if (event == undefined || Array.isArray(event.breakout_fireworks) == false) continue;

				if (my_firework_id != undefined) {
					const removal_index = event.breakout_fireworks.findIndex((breakout) => breakout.my_firework_id != undefined && breakout.my_firework_id == my_firework_id);
					if (removal_index >= 0) {
						event.breakout_fireworks.splice(removal_index, 1);
						change_made = true;
					}
				} else if (index != undefined && ids.length === 1) {
					// Fallback for single-event custom breakout removal (no firework id to match on)
					event.breakout_fireworks.splice(index, 1);
					change_made = true;
				}
			}
			if (change_made) this.setChangeMadeToTrue();
		},

		openDMXValueEditor({ event_id }) {
			this.dmx_value_editor_is_open_for_event_id = event_id;

			if (this.is_event_time_editor_open == true) {
				this.closeEventEditModal();
			}
		},
		closeDMXValueEditor: function () {
			this.dmx_value_editor_is_open_for_event_id = undefined;
		},

		async setCurrentOpenedShow(show, { callback = undefined } = {}) {
			const undo_manager = useUndoManagerStore();
			undo_manager.clearStack(); // to clear the undo/redo stack when show is changed.

			useWaveformStore().resetWaveformTimes();
			// resetValuesUsedForChannelCueCalculations(show.firmware_version);
			const validated_show = validateShow(show);
			this.active_show = validated_show; // resetValuesUsedForChannelCueCalculations will be called in validateShow(validateEvents)

			console.log('[EVS-981_CC_DEBUG] setCurrentOpenedShow LOADED', {
				show_id: this.active_show?.show_ID,
				custom_name: this.active_show?.custom_name,
				firmware_version: this.active_show?.firmware_version,
				event_count: this.active_show?.events?.length,
				events_summary_first_500: (this.active_show?.events || []).slice(0, 500).map((ev) => ({
					event_id: ev?.event_id,
					channel: ev?.channel,
					cue: ev?.cue,
					cue_auto_incremented: ev?.cue_auto_incremented,
					isDMX: ev?.isDMX,
				})),
			});

			createStepParentChildRelationship();
			this.setStepEventsList();
			syncSnappingOfStepEventsAfterPreviousEventIsEnded();

			// fetchAudioInformation is not awaited, so starting it here lets the audio request run in parallel with the firework sync below instead of queuing behind it, without changing when that await resolves for the callback.
			// on failure it throws but nothing awaits this call, so the rejection would otherwise vanish silently; explicitly clear info_about_loaded_song so anything waiting on it (e.g. the waveform) isn't stuck waiting on a value that will never change.
			this.fetchAudioInformation(this.active_show.audio_file_id).catch(() => {
				this.info_about_loaded_song = undefined;
			});

			// we have to await for syncing external firework IF EVENT LABELS ARE TO BE OPENED ON REFRESH PAGE + or quickly open the event labels modal after loading show..
			await this.syncExternalFireworksInStore();

			// this.setShouldHideLiftTimeColumn();
			// this.shouldHidePrefireColumn();
			this.next_step_event_index = show.events?.findIndex((event) => event.isSTEP === true); // used for pausing playback on step
			this.previous_step_event_index = show.events?.findIndex((event) => event.isSTEP === true); // used for pausing playback on step
			if (useLayoutStore().current_layout != 'OPERATOR_CONTROLS') {
				generateEventHighlightingTimeMarkers(show.events);
				resetLocalStates();
			}
			this.setChangeMadeToFalse();
			// for event highlighting time markers
			this.active_show_selected_events = [];
			useWaveformStore()?.resetSelectedAddEventState();
			this.last_save_show_hash = undefined;

			if (callback != undefined && typeof callback == 'function') {
				callback();
			}
		},
		async fetchAudioInformation(audio_file_id, { updated_audio_box_filename_in_attributes = false, updated_use_audio_box_in_attributes = false, updated_smpte_timecode_in_attributes = false } = {}) {
			if (audio_file_id != undefined) {
				try {
					let response = await getAudioInformation(audio_file_id);
					let retrieved_locally = false;
					if (response.error != undefined) {
						const locally_saved_audio_file_info = await _getRow('audio_files_info', audio_file_id);
						if (locally_saved_audio_file_info != undefined) {
							response = locally_saved_audio_file_info;
							retrieved_locally = true;
						} else {
							throw new Error(response.error);
						}
					}
					this.info_about_loaded_song = response;
					// if (this.active_show?.audio_file_id == undefined) {
					if (response.file_ID != undefined) {
						this.active_show.audio_file_id = response.file_ID;
						this.active_show.audio_box_filename = response.file_name;
						this.active_show.uses_audio = 1;

						if (updated_audio_box_filename_in_attributes == true) {
							this.active_show.show_attributes.audio_box_filename = response.file_name;
						}

						if (updated_use_audio_box_in_attributes == true) {
							this.active_show.show_attributes.use_audio_box = true;
						}

						if (updated_smpte_timecode_in_attributes != false) {
							this.active_show.show_attributes.smpte_timecode = updated_smpte_timecode_in_attributes;
						}

						if (retrieved_locally != true) {
							OfflineAudioManager.getAudioFileBlobFromURL(response.file_url).then((blob) => {
								OfflineAudioManager.updateAudioFile(null, blob, `${response.file_ID}`);
							});
							OfflineAudioManager.getAudioFileBlobFromURL(response.timecode_file_url).then((blob) => {
								OfflineAudioManager.updateAudioFile(null, blob, `${response.file_ID}_timecode`);
							});
							OfflineAudioManager.updateAudioFile(response);
						}
					}
				} catch (error) {
					throw new Error(JSON.stringify(error));
				}
			} else {
				this.clearAudioInformation();
			}
		},
		clearAudioInformation() {
			this.info_about_loaded_song = null;

			this.active_show.audio_file_id = undefined;
			this.active_show.audio_box_filename = undefined;
			this.active_show.uses_audio = 0;

			this.active_show.show_attributes.use_audio_box = false;
		},
		async reFetchActiveShowDetails() {
			if (this.active_show != undefined) {
				try {
					const response = await getShow(this.active_show.show_ID);
					if (response.error == undefined) {
						this.setCurrentOpenedShow(response.show);
					}
				} catch (error) {
					console.log('🚀 ~ file: show.js:81 ~ reFetchActiveShowDetail ~ error:', error);
				}
			} else {
				this.active_show = null;
			}
		},
		async syncExternalFireworksInStore() {
			try {
				const events = this.active_show.events;
				const firework_ids = [];
				// const promises = [];
				if (events != undefined && events.length > 0) {
					for (const event of events) {
						// Below comments were for testing error
						// if (event.my_firework_id == undefined) {
						// 	event.my_firework_id = 32490;
						// }
						if (event.my_firework_id != undefined && firework_ids.includes(event.my_firework_id) == false) {
							firework_ids.push(event.my_firework_id);
							// const firework_data_promise = getFireworkData(event.my_firework_id);
							// promises.push(firework_data_promise);
						}
					}
				}

				try {
					const firework_data = await getManyFireworkData(firework_ids);
					if (firework_data.length > 0) {
						const values = [];
						for (const firework of firework_data) {
							if (firework.status == 'success') {
								values.push(firework.data);
							}
						}
						const firework_store = useFireworkStore();
						firework_store.setMyFireworks(values);
					}

					// const firework_data = await Promise.allSettled(promises);
					// if (firework_data.length > 0) {
					// 	const values = [];
					// 	for (const firework of firework_data) {
					// 		if (firework.status == 'fulfilled') {
					// 			values.push(firework.value);
					// 		}
					// 	}
					// 	const firework_store = useFireworkStore();
					// 	firework_store.setMyFireworks(values);
					// }
				} catch (error) {
					console.log('error : ', error);
				}
			} catch (error) {
				console.log('Error syncing external fireworks:', error);
			}
		},
		clearCurrentOpenedShow() {
			this.active_show = null;
			this.active_show_order = null;
			this.info_about_loaded_song = null;
			useWaveformStore()?.clearAudioDuration();
		},
		deleteShow(selected_show, callback, activeShowDeletedCallback) {
			if (selected_show == undefined) {
				return;
			}

			let cancel_button = new Modal.Button('No', undefined, { inverted: true, border_inverted: true, bigger: true });
			let confirm_button = new Modal.Button(
				'Yes, Delete',
				async () => {
					//On click
					try {
						const response = await deleteShow(selected_show.show_ID);
						if (response.error != undefined) {
							NotificationCenter.add(new NotificationHandler.Notification('Error Deleting Show', `There was an error deleting this show.`, [], NotificationType.ERROR, { expire_time: 0 }));
							throw new Error(JSON.stringify(response.error));
						}

						if (selected_show.show_order_id != undefined) {
							await InventoryService.updateShowOrderState(selected_show.show_order_id, { state: SHOW_ORDER_STATUES.CANCELED?.key }).catch((error) => {
								console.log('error updating show order state to canceled', error);
							});
						}

						if (this.active_show?.show_ID == selected_show.show_ID) {
							// send to main route and clear the pinia store
							this.clearCurrentOpenedShow();
							activeShowDeletedCallback();
						}

						NotificationCenter.add(new NotificationHandler.Notification('Deleted Show', `The show was successfully deleted.`, [], NotificationType.SUCCESS, { has_close_button: true }));

						if (callback != undefined) {
							callback();
						}
					} catch (error) {
						console.error(error);
						NotificationCenter.add(new NotificationHandler.Notification('Error Deleting Show', `There was an error deleting this show.`, [], NotificationType.ERROR, { expire_time: 0 }));
						throw new Error(JSON.stringify(error));
					}
				},
				{ destructive: true, bigger: true, dismiss_modal_after_click: true, primary_action_button: true }
			);

			let html = '';
			const auth_store = useAuthStore();
			if (auth_store?.active_products.includes(PRODUCTS.INVENTORY_UPGRADE) == true) {
				html += `<span class="mt-1 rounded-md px-4 py-1 bg-red-200 text-red-700 text-sm text-left">Deleting this show will release any inventory items that are associated with it.</span>`;
			}

			Modal.createInteractiveModal(`Delete Show?`, `Are you sure you want to delete the show named "${selected_show.custom_name}"?`, html, [cancel_button, confirm_button], { destroy_on_dismissal: true });
		},
		deleteAudioFile(selected_audio_file, callback) {
			if (selected_audio_file == undefined) {
				return;
			}

			let cancel_button = new Modal.Button('No', undefined, { inverted: true, border_inverted: true, bigger: true });
			let confirm_button = new Modal.Button(
				'Yes, Delete',
				async () => {
					//On click

					try {
						const response = await deleteAudio(selected_audio_file.file_ID);
						if (response.error != undefined) {
							if (response.error.title == 'AUDIO_FILE_USED_IN_SHOW') {
								NotificationCenter.add(new NotificationHandler.Notification('Error Deleting File', 'The audio file is used in one or more shows. Unselect this file from the shows to delete it.', [], NotificationType.ERROR, { expire_time: 0 }));
							} else {
								NotificationCenter.add(new NotificationHandler.Notification('Error Deleting File', 'There was an error deleting this audio file.', [], NotificationType.ERROR, { expire_time: 0 }));
							}
							return;
						}

						// only the show's currently attached audio file should update active_show/show_attributes - deleting any other file in the library must not swap the show onto it
						if (selected_audio_file.file_ID == this.active_show?.audio_file_id) {
							this.fetchAudioInformation(selected_audio_file.file_ID);
							// fetchAudioInformation only updates show_attributes locally, it doesn't save them, so flag the show as dirty.
							this.setChangeMadeToTrue();
						}
						NotificationCenter.add(new NotificationHandler.Notification('Deleted Audio File', `The audio file was successfully deleted.`, [], NotificationType.SUCCESS, { has_close_button: true }));

						if (callback != undefined) {
							callback();
						}
					} catch (error) {
						console.error(error);
						if (error.title == 'AUDIO_FILE_USED_IN_SHOW') {
							NotificationCenter.add(new NotificationHandler.Notification('Error Deleting File', 'The audio file is used in one or more shows. Unselect this file from the shows to delete it.', [], NotificationType.ERROR, { expire_time: 0 }));
						} else {
							NotificationCenter.add(new NotificationHandler.Notification('Error Deleting File', 'There was an error deleting this audio file.', [], NotificationType.ERROR, { expire_time: 0 }));
						}
						throw new Error(JSON.stringify(error));
					}
				},
				{ destructive: true, bigger: true, dismiss_modal_after_click: true, primary_action_button: true }
			);

			Modal.createInteractiveModal(`Delete Audio File?`, `Are you sure you want to delete the audio file named "${selected_audio_file.file_name}"?`, null, [cancel_button, confirm_button], { destroy_on_dismissal: true });
		},
		async duplicateShow(selected_show) {
			if (selected_show != undefined) {
				try {
					const response = await duplicateShow(selected_show.show_ID);
					if (response.error != undefined) {
						throw new Error(JSON.stringify(response.error));
					}
					return response;
				} catch (error) {
					console.log('🚀 ~ file: show.js:268 ~ duplicateShow ~ error:', error);
					throw new Error(JSON.stringify(error));
				}
			}
		},

		updateEventChannel(event, new_channel, { skip_used_cues_and_validation = false } = {}) {
			if (this.is_show_locked == true) {
				console.log('[EVS-981_CC_DEBUG] updateEventChannel SKIPPED (show locked)', { event_id: event?.event_id, new_channel });
				return;
			}

			const settings_store_for_log = useSettingStore();
			console.log('[EVS-981_CC_DEBUG] updateEventChannel ENTER', {
				event_id: event?.event_id,
				prev_channel: event?.channel,
				prev_channel_type: typeof event?.channel,
				new_channel_raw: new_channel,
				new_channel_type: typeof new_channel,
				prev_cue: event?.cue,
				cue_auto_incremented: event?.cue_auto_incremented,
				isDMX: event?.isDMX,
				auto_increment_cues_setting: settings_store_for_log?.settings?.auto_increment_cues,
				will_short_circuit: event?.channel == new_channel,
			});

			if (event.channel != new_channel) {
				const previous_channel = event.channel;
				event.channel = new_channel;

				// if no value is inputted and is left blank remove channel cue from used cue to undefined
				if (typeof new_channel == 'string' && new_channel.trim() == '') {
					event.channel = undefined;
					console.log('[EVS-981_CC_DEBUG] updateEventChannel normalized blank string -> undefined', { event_id: event?.event_id });
				}

				// Bulk callers (e.g. AutoAddressModal) address many events in a loop and run one validateEvents() pass over the whole show afterwards, so the per-event used_cues bookkeeping/validation here would just be recomputed and discarded.
				if (skip_used_cues_and_validation == true) {
					this.setChangeMadeToTrue();
					return;
				}

				const settings_store = useSettingStore();
				removeUsedChannelFromUsedCues(previous_channel, event, this.active_show);

				if (settings_store?.settings?.auto_increment_cues === true && (event?.cue_auto_incremented == undefined || event.cue_auto_incremented == true) && event.isDMX !== true) {
					console.log('[EVS-981_CC_DEBUG] updateEventChannel AUTO-INCREMENT BRANCH entered', {
						event_id: event?.event_id,
						previous_channel,
						new_channel_passed_to_getNext: new_channel,
					});
					event.cue = undefined;
					updateCuesUsedPerChannel(this.active_show.events);
					const previous_cue = event.cue;
					const cue_for_channel = getNextCueForChannel(new_channel);
					console.log('[EVS-981_CC_DEBUG] updateEventChannel got cue_for_channel', {
						event_id: event?.event_id,
						cue_for_channel,
					});

					this.updateEventCue(event, cue_for_channel, true);
					console.log('[EVS-981_CC_DEBUG] updateEventChannel EXIT (auto-increment branch)', {
						event_id: event?.event_id,
						final_channel: event?.channel,
						final_cue: event?.cue,
						final_cue_auto_incremented: event?.cue_auto_incremented,
					});
					if (previous_channel == undefined || previous_channel == '') {
						validateEvent(this.active_show, event, undefined, undefined, undefined, true);
					} else {
						validateEvent(this.active_show, event, undefined, undefined, previous_channel, false, previous_cue);
					}
					return;
				}
				console.log('[EVS-981_CC_DEBUG] updateEventChannel NON-auto-increment branch (cue preserved)', {
					event_id: event?.event_id,
					final_channel: event?.channel,
					preserved_cue: event?.cue,
					reason_no_auto_increment: {
						auto_increment_setting: settings_store?.settings?.auto_increment_cues,
						cue_auto_incremented: event?.cue_auto_incremented,
						isDMX: event?.isDMX,
					},
				});
				if (previous_channel == undefined || previous_channel == '') {
					validateEvent(this.active_show, event, undefined, undefined, undefined, true);
				} else {
					validateEvent(this.active_show, event, undefined, undefined, previous_channel);
				}
				this.setChangeMadeToTrue();
			}
		},

		//Also updated the value in case of dmx event
		updateEventCue(event, new_cue, updated_cue_for_channel = false, { skip_used_cues_and_validation = false } = {}) {
			if (this.is_show_locked == true) {
				console.log('[EVS-981_CC_DEBUG] updateEventCue SKIPPED (show locked)', { event_id: event?.event_id, new_cue });
				return;
			}
			const previous_cue = event.cue;
			const previous_cue_auto_incremented = event?.cue_auto_incremented;
			console.log('[EVS-981_CC_DEBUG] updateEventCue ENTER', {
				event_id: event?.event_id,
				channel: event?.channel,
				prev_cue: previous_cue,
				new_cue_raw: new_cue,
				new_cue_type: typeof new_cue,
				prev_cue_auto_incremented: previous_cue_auto_incremented,
				updated_cue_for_channel,
			});

			// if no value is inputted and is left blank set cue to undefined
			if (typeof new_cue == 'string' && new_cue.trim() == '') {
				if (event.cue != undefined) {
					event.cue = undefined;
					event.cue_auto_incremented = true;
					this.setChangeMadeToTrue();
					// Bulk callers (e.g. AutoAddressModal) run one validateEvents() pass over the whole show after their loop, so this per-event bookkeeping/validation would just be recomputed and discarded.
					if (skip_used_cues_and_validation == true) {
						return;
					}
					//remove used cue
					removeUsedCueFromChannel(previous_cue, event, this.active_show);
					validateEvent(this.active_show, event);
				}
				console.log('[EVS-981_CC_DEBUG] updateEventCue EXIT (blank input)', {
					event_id: event?.event_id,
					final_cue: event?.cue,
					final_cue_auto_incremented: event?.cue_auto_incremented,
				});
				return;
			}

			event.cue = new_cue;
			this.setChangeMadeToTrue();

			if (updated_cue_for_channel != true) {
				event.cue_auto_incremented = false;
				if (skip_used_cues_and_validation == true) {
					return;
				}
				if (previous_cue == undefined || previous_cue == '') {
					validateEvent(this.active_show, event, undefined, previous_cue, undefined, undefined, undefined, true);
				} else {
					validateEvent(this.active_show, event, undefined, previous_cue);
				}
			}
			console.log('[EVS-981_CC_DEBUG] updateEventCue EXIT', {
				event_id: event?.event_id,
				final_cue: event?.cue,
				final_cue_auto_incremented: event?.cue_auto_incremented,
				updated_cue_for_channel,
			});
		},

		updateEventDMXRampFromValue({ event, new_dmx_ramp_from_value }) {
			if (this.is_show_locked == true) {
				return;
			}

			// if no value is inputted and is left blank set new_dmx_ramp_from_value to undefined
			if (typeof new_dmx_ramp_from_value == 'string' && new_dmx_ramp_from_value.trim() == '') {
				if (event.dmx_ramp_from_value != undefined) {
					event.dmx_ramp_from_value = undefined;
					this.setChangeMadeToTrue();
					validateEvent(this.active_show, event);
				}
				return;
			}

			event.dmx_ramp_from_value = new_dmx_ramp_from_value;
			this.setChangeMadeToTrue();

			validateEvent(this.active_show, event);
		},

		updateEventDescription(event, new_description) {
			if (this.is_show_locked == true) {
				return;
			}

			event.firework_name = new_description;
			event.my_firework_id = undefined;
			event.inventory_item_id = undefined;
			this.setChangeMadeToTrue();

			this.newly_updated_selected_events = [event];
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.updateEventDescription;

			validateEvent(this.active_show, event);
			useInventoryStore()?.setTriggerFindInventoryChanges();
		},
		updateEventCustomCell(event, custom_column, new_custom_value) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.custom_values == undefined) {
				event.custom_values = {};
			}
			event.custom_values[custom_column] = new_custom_value;

			this.setChangeMadeToTrue();
			validateEvent(this.active_show, event);
		},

		validateCellOnValueChange(event) {
			const event_index = this.active_show.events.findIndex((e) => event.event_id === e.event_id);
			removeErrorsAndWarningsFromEvent(event_index, this.active_show.events);
			// this.active_show.events = validateEventElement(event, this.active_show); # after the updated made in commit 5467413, there's no need to receive the events
			validateEventElement(event, this.active_show);
		},
		updateEventDuration(event, new_duration) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.duration_in_seconds !== new_duration && new_duration !== event.duration) {
				event.duration = isNaN(new_duration) ? new_duration : Number(new_duration) + 's';
				event.duration_in_seconds = isNaN(new_duration) ? null : Number(new_duration);
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateEventDuration;
				validateEvent(this.active_show, event);

				// update the relative times and event times for the step events below the updated event.
				updateOffsetsAndRelativeTime({ updated_event: event, event_to_which_step_event_is_snapped_to: undefined, sorting_done_due_to_reduced_event_time: false, called_due_to_change_in_duration: true });
			}
		},
		updateEventPreFire(event, new_pre_fire) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.igniter_pre_fire_in_seconds !== new_pre_fire && new_pre_fire !== event.igniter_pre_fire) {
				event.igniter_pre_fire = isNaN(new_pre_fire) ? new_pre_fire : Number(new_pre_fire) + 's';
				event.igniter_pre_fire_in_seconds = isNaN(new_pre_fire) ? null : Number(new_pre_fire);
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateEventPreFire;

				// validate whole row to remove the warnings/errors effected by this column
				event.negative_calculated_event_time = calculateEventTimeForEvent(event, this.active_show.firmware_version).negative_calculated_time;
				validateEvent(this.active_show, event);
				this.setShouldHidePrefireColumn();
			}
		},
		updateEventLiftTime(event, new_lift_time) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.lift_time_in_seconds !== new_lift_time && new_lift_time !== event.lift_time) {
				event.lift_time = isNaN(new_lift_time) ? new_lift_time : Number(new_lift_time) + 's';
				event.lift_time_in_seconds = isNaN(new_lift_time) ? null : Number(new_lift_time);
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateEventLiftTime;

				// validate whole row to remove the warnings/errors effected by this column
				event.negative_calculated_event_time = calculateEventTimeForEvent(event, this.active_show.firmware_version).negative_calculated_time;
				validateEvent(this.active_show, event);

				this.setShouldHideLiftTimeColumn();
			}
		},
		updateEventPulseTime(event, new_pulse_time) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.pulse !== new_pulse_time) {
				event.pulse = new_pulse_time === '' || new_pulse_time == null ? undefined : isNaN(new_pulse_time) ? new_pulse_time : Number(new_pulse_time);
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateEventPulseTime;
				validateEvent(this.active_show, event);
			}
		},
		updateEventUniverse(event, new_universe) {
			if (this.is_show_locked == true) {
				return;
			}
			if (event.universe !== new_universe) {
				event.universe = new_universe;
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateEventUniverse;
				validateEvent(this.active_show, event);
			}
		},
		updateEventQuantity(event, new_quantity) {
			if (this.is_show_locked == true) {
				return;
			}
			if (event.quantity !== new_quantity) {
				event.quantity = new_quantity;
				this.setChangeMadeToTrue();
				this.newly_updated_selected_events = [event];
				this.action_being_performed = SHOW_METHODS.updateEventQuantity;
				validateEvent(this.active_show, event);
			}
		},
		// Defined position and disable firing group are assigned from their own modals rather than
		// an inline cell, but they go through the same per-event actions so that the lock check and
		// the waveform/undo side effects stay in one place. Neither field affects event timing, so
		// there is no validateEvent call here.
		updateEventDefinedPosition(event, new_defined_position) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.defined_position?.id !== new_defined_position?.id) {
				event.defined_position = new_defined_position;
				this.setChangeMadeToTrue();
				this.action_being_performed = SHOW_METHODS.updateEventDefinedPosition;
			}
		},
		updateEventDisableFiringGroup(event, new_disable_firing_group) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event.disable_firing_group?.id !== new_disable_firing_group?.id) {
				event.disable_firing_group = new_disable_firing_group;
				this.setChangeMadeToTrue();
				this.action_being_performed = SHOW_METHODS.updateEventDisableFiringGroup;
			}
		},

		applyValueToSelectedEvents({ column_cell, value, custom_column = undefined }) {
			if (this.is_show_locked == true) {
				return;
			}

			const selected_ids = this.active_show_selected_events || [];
			if (selected_ids.length <= 1) {
				return;
			}

			const { base_column } = parseMultiEditColumnCell(column_cell);
			const selected_set = new Set(selected_ids);
			const target_events = this.active_show.events.filter((event) => selected_set.has(event.event_id) && doesMultiEditColumnApply(base_column, event));
			if (target_events.length === 0) {
				return;
			}

			// Duration cascades onto other rows, so its undo needs the whole array; all other
			// columns only touch the targeted rows.
			const cascades = MULTI_EDIT_CASCADING_COLUMNS.has(base_column);
			const snapshot = () => JSON.parse(JSON.stringify(cascades ? this.active_show.events : target_events));

			const original_events = snapshot();

			for (const event of target_events) {
				switch (base_column) {
					case MULTI_EDIT_COLUMNS.UNIVERSE:
						this.updateEventUniverse(event, value);
						break;
					case MULTI_EDIT_COLUMNS.CHANNEL:
						this.updateEventChannel(event, value);
						break;
					case MULTI_EDIT_COLUMNS.CUE:
						this.updateEventCue(event, value);
						break;
					case MULTI_EDIT_COLUMNS.DESCRIPTION:
						this.updateEventDescription(event, value);
						break;
					case MULTI_EDIT_COLUMNS.DURATION:
						this.updateEventDuration(event, value);
						break;
					case MULTI_EDIT_COLUMNS.PRE_FIRE:
						this.updateEventPreFire(event, value);
						break;
					case MULTI_EDIT_COLUMNS.LIFT_TIME:
						this.updateEventLiftTime(event, value);
						break;
					case MULTI_EDIT_COLUMNS.PULSE:
						this.updateEventPulseTime(event, value);
						break;
					case MULTI_EDIT_COLUMNS.DEFINED_POSITION:
						this.updateEventDefinedPosition(event, value);
						break;
					case MULTI_EDIT_COLUMNS.DISABLE_FIRING_GROUP:
						this.updateEventDisableFiringGroup(event, value);
						break;
					case MULTI_EDIT_COLUMNS.CUSTOM:
						if (custom_column != undefined) {
							this.updateEventCustomCell(event, custom_column, value);
						}
						break;
					default:
						break;
				}
			}

			// Fire the shared side effects once for the whole batch. Columns that cannot move an event
			// in time skip the waveform refresh entirely.
			if (MULTI_EDIT_NON_WAVEFORM_COLUMNS.has(base_column) === false) {
				this.newly_updated_selected_events = target_events;
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
			}
			this.setChangeMadeToTrue();

			// Single grouped undo entry. `defaultCallback` restores each event by event_id, so both
			// the partial (per-row) and full (cascading) snapshots work with it.
			const post_update_events = snapshot();
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events);
		},
		async loadActiveShowStats() {
			if (this.active_show != undefined) {
				const response = await getShowStats(this.active_show);
				if (response != undefined) {
					this.active_show_stats = response;
				}
			}
		},
		updateEventComment(event, comment) {
			if (this.is_show_locked == true) {
				return;
			}

			if (event?.comment?.trim() !== comment?.trim()) {
				event.comment = comment;
				this.setChangeMadeToTrue();
			}
		},
		selectAnEvent({ e, event_id, firework_name, select_multiple = false, selected_using_waveform = false, do_not_seek_audio_playback = false, select_all_events = false } = {}) {
			if (select_all_events == true) {
				this.active_show_selected_events = this.active_show.events.map((event) => event.event_id);
				this.active_show_selected_fireworks = this.active_show.events.map((event) => event.firework_name);

				addSelectedRowBackgroundColorToAllEvents();

				return;
			}

			if (e?.shiftKey) {
				let starting_event_index = undefined;
				let ending_event_index = this.active_show.events.findIndex((event) => event.event_id == event_id);

				if (this.active_show_selected_events.length > 0) {
					let starting_event_id = this.active_show_selected_events[this.active_show_selected_events.length - 1];
					starting_event_index = this.active_show.events.findIndex((event) => event.event_id == starting_event_id);
				} else {
					starting_event_index = ending_event_index;
				}

				let smaller_index = Math.min(starting_event_index, ending_event_index);
				let larger_index = Math.max(starting_event_index, ending_event_index);

				for (let index = smaller_index; index <= larger_index; index++) {
					const event_id = this.active_show.events[index].event_id;
					const firework_name = this.active_show.events[index].firework_name;

					if (!this.active_show_selected_events.includes(event_id)) {
						this.active_show_selected_events.push(event_id);
						if (!this.active_show_selected_fireworks.includes(firework_name)) {
							this.active_show_selected_fireworks.push(firework_name);
						}
						addSelectedRowBackgroundColor(event_id);
					}
				}
				this.active_show_selected_events = sortSelectedEventsByRowIndex(this.active_show_selected_events);
			} else if (e?.ctrlKey || e?.metaKey || select_multiple == true) {
				// to unselect event
				if (this.active_show_selected_events.includes(event_id)) {
					clearEventBackgroundColor(event_id);
					const index = this.active_show_selected_events.indexOf(event_id);
					this.active_show_selected_events.splice(index, 1);
					const firework_index = this.active_show_selected_fireworks.indexOf(firework_name);
					if (firework_index !== -1) {
						this.active_show_selected_fireworks.splice(firework_index, 1);
					}
				} else {
					this.active_show_selected_events.push(event_id);
					if (!this.active_show_selected_fireworks.includes(firework_name)) {
						this.active_show_selected_fireworks.push(firework_name);
					}
					addSelectedRowBackgroundColor(event_id);
				}
				this.active_show_selected_events = sortSelectedEventsByRowIndex(this.active_show_selected_events);
			} else {
				clearSelectedRowsBackgroundColor();
				this.active_show_selected_events = [event_id];
				this.active_show_selected_fireworks = [firework_name];
				this.do_not_seek_audio_playback = !!do_not_seek_audio_playback;

				addSelectedRowBackgroundColor(event_id);
			}
			// only perform event selection on clicking event index column or selected from waveform
			if (this.active_show_selected_column_cell == 'EventIndex' || selected_using_waveform == true) {
				const found_event = this.active_show.events.find((event) => event.event_id == this.active_show_selected_events[0]);
				if (found_event != undefined) {
					useWaveformStore()?.setCurrentPlaybackTime(found_event.timeline_event_time);
				}
			}
		},
		async handleCopy(e, selected_text) {
			console.log('this.user_is_editing: ', this.user_is_editing);
			if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false) {
				return;
			}

			if (this.user_is_editing == false) {
				// for internal copy pasting of elevate
				this.copied_events = this.active_show?.events?.filter((event) => this.active_show_selected_events.includes(event.event_id));
				// for external copy use cases
				// Reuse the same rules the table uses to show/hide these columns, so copy/paste can't drift from what's actually editable.
				const hide_pre_fire_column_for_show = shouldHidePrefireColumn();
				const hide_lift_time_column_for_show = shouldHideLiftTimeColumn();
				const hide_disable_firing_group_column_for_show = shouldHideDisableFiringGroupColumn();
				const hide_pulse_time_column_for_show = shouldHidePulseTimeColumn();
				const hide_quantity_column_for_show = shouldHideQuantityColumn();

				let copy_data = `EVENT_TIME\tCHANNEL\tCUE\tDESCRIPTION${hide_quantity_column_for_show == false ? '\tQUANTITY' : ''}\tDURATION${hide_pre_fire_column_for_show == false ? '\tPRE_FIRE' : ''}${hide_lift_time_column_for_show == false ? '\tLIFT_TIME' : ''}${hide_pulse_time_column_for_show == false ? '\tPULSE' : ''}\tIS_STEP${hide_disable_firing_group_column_for_show == false ? '\tDISABLE_FIRING_GROUP' : ''}\tCUSTOM_COLUMNS`;

				// Non-pyro shows prepend the EVENT_TYPE and UNIVERSE columns. Every other column stays in the same order as the pyro header above, so the row loop below can emit one shared sequence.
				if (this.active_show.show_attributes.show_type != SHOW_TYPES.PYRO.key) {
					copy_data = `EVENT_TYPE\tEVENT_TIME\tUNIVERSE\tCHANNEL\tCUE\tDESCRIPTION${hide_quantity_column_for_show == false ? '\tQUANTITY' : ''}\tDURATION${hide_pre_fire_column_for_show == false ? '\tPRE_FIRE' : ''}${hide_lift_time_column_for_show == false ? '\tLIFT_TIME' : ''}${hide_pulse_time_column_for_show == false ? '\tPULSE' : ''}\tIS_STEP${hide_disable_firing_group_column_for_show == false ? '\tDISABLE_FIRING_GROUP' : ''}\tCUSTOM_COLUMNS`;
				}

				for (const event of this.copied_events) {
					if (event == undefined) {
						return;
					}

					copy_data += `\n`;
					let event_type = undefined;
					if (this.active_show.show_attributes.show_type != SHOW_TYPES.PYRO.key) {
						event_type = event.isDMX == true ? 'DMX' : 'PYRO';
					} //If not a Pyro only show add a event type prefix

					let event_time = event.event_time;

					//Discuss: event.isSTEP was removed to pass accurate time for step events
					if (event.isALTERNATE1 || event.isALTERNATE2) {
						event_time = calculateEventTimeForEvent(event).event_time;
					} //Handle none timed event times

					let universe_value = undefined;
					if (event.isDMX) {
						universe_value = event.universe;
					}

					let channel_value = event.channel;
					if (event.isCOMMENT) {
						channel_value = 'COMMENT';
					}

					let event_description = event.firework_name;
					if (event.isCOMMENT) {
						event_description = event.comment;
					}

					if (this.active_show.show_attributes.show_type != SHOW_TYPES.PYRO.key) {
						copy_data += `${event_type || 'PYRO'}\t${event_time || ''}\t${universe_value || ''}\t${channel_value != undefined ? channel_value : ''}\t${event.cue != undefined ? event.cue : ''}\t${event_description || ''}${hide_quantity_column_for_show == false ? `\t${event.quantity != undefined ? event.quantity : ''}` : ''}\t${event.duration || ''}`;
					} else {
						copy_data += `${event_time || ''}\t${channel_value != undefined ? channel_value : ''}\t${event.cue != undefined ? event.cue : ''}\t${event_description || ''}${hide_quantity_column_for_show == false ? `\t${event.quantity != undefined ? event.quantity : ''}` : ''}\t${event.duration || ''}`;
					}

					if (hide_pre_fire_column_for_show == false) {
						copy_data += `\t${event.igniter_pre_fire || ''}`;
					}
					if (hide_lift_time_column_for_show == false) {
						copy_data += `\t${event.lift_time || ''}`;
					}

					if (hide_pulse_time_column_for_show == false) {
						copy_data += `\t${event.pulse || ''}`;
					}

					copy_data += `\t${event.isSTEP ? 'true' : 'false'}`;

					if (hide_disable_firing_group_column_for_show == false) {
						// outer: is column in header?
						if (this.active_show.firmware_version >= 7.0 && event.disable_firing_group != undefined) {
							copy_data += `\t${event.disable_firing_group.label}_${event.disable_firing_group.color}`;
						} else {
							copy_data += `\t`; // column in header but event has no value → empty cell
						}
					}

					for (var column = 0; column < this.active_show.custom_columns?.length; column++) {
						var custom_column_title = this.active_show.custom_columns[column];
						var custom_column_values = event.custom_values ? event.custom_values : {};
						copy_data += `\t${custom_column_values[custom_column_title] ? custom_column_values[custom_column_title] : ''}`;
					}
				}

				if (e.type != 'copy') {
					try {
						await navigator.clipboard.writeText(copy_data);
					} catch (error) {
						console.error('Failed to write clipboard contents: ', error);
					}
				} else {
					e.clipboardData.setData('text/plain', copy_data);
					e.preventDefault();
				}
			} else {
				// Manually trigger the copy event
				let focus_row_cell_id = document.activeElement.id;
				if (focus_row_cell_id == undefined || focus_row_cell_id == '') {
					focus_row_cell_id = this.context_menu_opened_on_focused_cell_id;
				}
				// const element_to_focused = document.getElementById(focus_row_cell_id);
				// element_to_focused.focus();
				if (selected_text != undefined) {
					await navigator.clipboard.writeText(selected_text);
					document.execCommand('copy');
				}
			}
		},
		async pasteEvent(e, { from_shortcut = false } = {}) {
			if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false) {
				return;
			}

			if (this.is_show_locked == true) {
				return;
			}
			if (this.active_show == undefined) {
				return;
			}
			const loader_store = useLoaderStore();
			// The loader is turned off in the finally block rather than on each return path, so a throw
			// anywhere in the paste cannot leave the table stuck behind a spinner.
			loader_store.toggleEventTableLoader(true);
			try {
				// conditionally set user_is_editing flag to true because some columns like s.no & event time do not have an id or have rowIndex in their ids, so we can utilize that to conditionally update the value of user_is_editing to true.
				// true is required if user is pasting multiple rows of same column which is only possible on those columns (in elevate) which are assigned dynamic ids by us.
				let focus_row_cell_id = document.activeElement.id;
				if (focus_row_cell_id == undefined || focus_row_cell_id == '') {
					focus_row_cell_id = this.context_menu_opened_on_focused_cell_id;
				}
				if (focus_row_cell_id == undefined || focus_row_cell_id == '' || focus_row_cell_id.includes('rowIndex')) {
					this.user_is_editing = false;
				} else {
					this.user_is_editing = true;
				}
				let text = '';
				if (e == undefined || e.type != 'paste') {
					text = await navigator.clipboard.readText();
				} else {
					text = e.clipboardData.getData('text/plain');
				}
				text = text.trim();
				const number_of_columns = text.split(/,|\t/).length - 1;
				if (this.user_is_editing == false && number_of_columns < 2) {
					NotificationCenter.add(new NotificationHandler.Notification('Pasting non row content', `It looks like you are trying to paste one or more rows, however, the clipboard content does not contain multiple columns and can not be pasted as whole rows.`, [], NotificationType.INFO));
					return;
				}

				let lines = text.split('\n');
				console.log('[EVS-981_CC_DEBUG] pasteEvent ENTER', {
					focus_row_cell_id,
					user_is_editing: this.user_is_editing,
					num_lines: lines.length,
					lines_preview: lines.slice(0, 30),
					number_of_columns,
					selected_events_count: this.active_show_selected_events?.length,
					from_shortcut,
				});
				let header_components = undefined;

				const original_events = JSON.parse(JSON.stringify(this.active_show.events));

				// Reset the shared column position maps so a previous paste's header can't leak into this one.
				for (const key of Object.keys(column_key_for_index)) {
					delete column_key_for_index[key];
				}
				for (const key of Object.keys(column_index_for_key)) {
					delete column_index_for_key[key];
				}
				Object.assign(column_key_for_index, DEFAULT_COLUMN_KEY_FOR_INDEX);
				Object.assign(column_index_for_key, DEFAULT_COLUMN_INDEX_FOR_KEY);

				//Contains a header row
				if (lines[0].substring(0, 10) == 'EVENT_TIME' || lines[0].substring(0, 10) == 'EVENT_TYPE') {
					header_components = lines.shift().split(/,|\t/); // Shift removes first element of array, then split by either \t or ,
					for (const [index, header_component] of header_components.entries()) {
						if (COLUMN_KEYS[header_component] == undefined) {
							continue;
						}
						column_index_for_key[index] = COLUMN_KEYS[header_component];
						column_key_for_index[COLUMN_KEYS[header_component]] = index;
					}
				}
				//Trying to insert whole rows
				if (this.user_is_editing == false && this.active_show_selected_events?.length >= 1) {
					// const start_time = new Date().getTime();
					this.current_selected_row_index_for_pasting = -1; // just to make sure it has no predefined value

					// Pre-calculate these values outside the loop
					const index_of_event_id = returnIndexOfEventIds(this.active_show.events);
					const starting_index = index_of_event_id[this.active_show_selected_events[0]];
					const event = this.active_show.events[starting_index];
					let index_to_be_below = index_of_event_id[event.event_id] + 1;

					// Pre-calculate column offset outside the loop
					let column_offset = 6;
					if (COLUMN_KEYS.CUSTOM_COLUMNS != undefined && column_key_for_index[COLUMN_KEYS.CUSTOM_COLUMNS] != undefined) {
						column_offset = column_key_for_index[COLUMN_KEYS.CUSTOM_COLUMNS] - 1;
					} else {
						if (column_key_for_index[COLUMN_KEYS.PRE_FIRE] == undefined) {
							column_offset -= 1;
						}
						if (column_key_for_index[COLUMN_KEYS.LIFT_TIME] == undefined) {
							column_offset -= 1;
						}
					}

					// Pre-calculate store references outside the loop
					const auth_store = useAuthStore();
					const undo_manager = useUndoManagerStore();
					const inventory_store = useInventoryStore();

					// Force UI update after showing loader - use a small delay just to show the loader
					await new Promise((resolve) => setTimeout(resolve, 50));

					// STEP times are calculated once after the loop, so remember whether any STEP row was pasted.
					let any_step_event_was_pasted = false;
					// The row the table scrolls to and selects once the whole clipboard is in place.
					let last_added_event = undefined;
					// Counted so one notification can summarize the whole paste instead of one per bad row.
					let rows_skipped_for_invalid_event_time = 0;

					for (let i = 0; i < lines.length; i++) {
						let columns = lines[i].split(/,|\t/);

						let event_type = ADD_EVENT_BUTTON_STATES.PYRO;

						if (COLUMN_KEYS.EVENT_TYPE != undefined && columns[column_key_for_index[COLUMN_KEYS.EVENT_TYPE]] == ADD_EVENT_BUTTON_STATES.DMX) {
							event_type = ADD_EVENT_BUTTON_STATES.DMX;
						}

						// Ensure DMX events are not pasted into a Pyro show and vice versa. DMX support starts at
						// firmware 6.0, which is the same threshold filtered_events_based_on_show_type uses.
						if (this.active_show.firmware_version < 6 && event_type.toLowerCase() == EVENT_TYPES.DMX) {
							continue;
						} else {
							if (this.active_show.show_attributes.show_type == SHOW_TYPES.PYRO.key && event_type.toLowerCase() == EVENT_TYPES.DMX) {
								continue;
							}
							if (this.active_show.show_attributes.show_type == SHOW_TYPES.DMX.key && event_type.toLowerCase() != EVENT_TYPES.DMX) {
								continue;
							}
						}

						let original_event_time_value = columns[column_key_for_index[COLUMN_KEYS.EVENT_TIME]];

						let timeline_event_time_of_pasted_event = columns[column_key_for_index[COLUMN_KEYS.EVENT_TIME]] != undefined ? toSeconds(getSafeString(columns[column_key_for_index[COLUMN_KEYS.EVENT_TIME]])) : 0;
						let is_non_timed_event = false;

						// STEP is added here incase an event is copied from an older source e.g. CSC or Export file and its event_time value is STEP
						let non_timed_event_times = ['STEP', 'ALTERNATE', 'ALTERNATE2'];

						// This will only be true if copied a step event from elevate else it will be false
						const is_copied_event_step = columns[column_key_for_index[COLUMN_KEYS.IS_STEP]] === 'true';

						if (is_copied_event_step == true) {
							event_type = ADD_EVENT_BUTTON_STATES.STEP;
						}
						if (event_type == ADD_EVENT_BUTTON_STATES.STEP || original_event_time_value == 'STEP') {
							any_step_event_was_pasted = true;
						}

						let disable_firing_group;
						if (this.active_show.firmware_version >= 7.0) {
							const copied_disable_firing_group = columns[column_key_for_index[COLUMN_KEYS.DISABLE_FIRING_GROUP]] != undefined ? columns[column_key_for_index[COLUMN_KEYS.DISABLE_FIRING_GROUP]] : '';
							if (copied_disable_firing_group != undefined && copied_disable_firing_group != '') {
								// const auth_store = useAuthStore();
								const fallback_color = '#bec2c8';
								const fallback_label = this.active_show?.disable_firing_groups?.[0]?.label || 'Anonymous';

								disable_firing_group = {
									label: copied_disable_firing_group.split('_')?.[0] ?? fallback_label,
									color: copied_disable_firing_group.split('_')?.[1] ?? fallback_color,
									id: new Date().getTime() + Math.random().toString(16).slice(-4), // new date().getTime() will be used as the ID
									created_on: new Date().getTime(), // new Date().getTime()
									created_by: auth_store.user_info.UUID, // Current user's UUID
									updated_on: new Date().getTime(), // new Date().getTime()
									updated_by: auth_store.user_info.UUID, // Current user's UUID
								};
							}
						}

						if (non_timed_event_times.indexOf(original_event_time_value) != -1 || columns[column_key_for_index[COLUMN_KEYS.CHANNEL]] == 'COMMENT') {
							is_non_timed_event = true;

							if (original_event_time_value == 'STEP') {
								//Get timeline_event_time of first selected event
								timeline_event_time_of_pasted_event = this.active_show_selected_events[0].timeline_event_time;
							} else if (columns[column_key_for_index[COLUMN_KEYS.CHANNEL]] != 'COMMENT') {
								timeline_event_time_of_pasted_event = 0;
							}

							if (original_event_time_value == 'ALTERNATE' || original_event_time_value == 'ALTERNATE2') {
								let lowest_index_alternate_can_be_inserted = 0; //Confirm index_to_be_below is not above the max insert line
								for (let i = this.active_show.events.length - 1; i >= 0; i--) {
									let alternate_event = this.active_show.events[i];
									if (alternate_event.isALTERNATE1 == false && alternate_event.isALTERNATE2 == false) {
										lowest_index_alternate_can_be_inserted = i + 1;
										break;
									}
								}

								if (index_to_be_below <= lowest_index_alternate_can_be_inserted) {
									index_to_be_below = lowest_index_alternate_can_be_inserted + 1;
								}
							}
						}

						// STEP/ALTERNATE/ALTERNATE2/COMMENT rows are non-timed and were already given a valid
						// timeline_event_time above, so only a timed row can still be carrying an unparseable
						// value here (e.g. a non-numeric event_time column). Skip it rather than pasting a row
						// at a fabricated time.
						if (is_non_timed_event == false && isNaN(timeline_event_time_of_pasted_event)) {
							rows_skipped_for_invalid_event_time++;
							continue;
						}

						if (this.active_show_selected_events?.length - 1 > this.current_selected_row_index_for_pasting) {
							this.current_selected_row_index_for_pasting++;
						}
						let new_event = this.addNewEvent({ selected_add_event_state: event_type, dropped_event_time: timeline_event_time_of_pasted_event, add_undo_redo: false, from_paste: true });

						// addNewEvent returns nothing when the show has hit an event cap, and no later row can
						// be added either. Break rather than return, so the rows that did land still get sorted
						// and registered for undo below.
						if (index_to_be_below < 0 || new_event == undefined || new_event == null) {
							break;
						}

						last_added_event = new_event;

						if (is_non_timed_event == true || is_copied_event_step == true) {
							if (original_event_time_value == 'ALTERNATE' || original_event_time_value == 'ALTERNATE2') {
								new_event[original_event_time_value == 'ALTERNATE2' ? 'isALTERNATE2' : 'isALTERNATE1'] = true;
							} else if (original_event_time_value == 'STEP' || is_copied_event_step == true) {
								// skipping step calculations
								new_event.isSTEP = true;
							} else if (columns[column_key_for_index[COLUMN_KEYS.CHANNEL]] == 'COMMENT') {
								//Check in the channel column for COMMENT
								new_event.isCOMMENT = true;
								new_event.comment = columns[column_key_for_index[COLUMN_KEYS.DESCRIPTION]]; //Firework_name column / comment column
							} else {
								console.log('ERROR, unrecognized EVENT TIME');
							}
						}

						if (disable_firing_group != undefined && this.active_show.firmware_version >= 7.0) {
							new_event.disable_firing_group = disable_firing_group;
						}

						let custom_column_offset = undefined;
						for (let i = 1; i < columns.length; i++) {
							if (i <= column_offset) {
								let column_value = columns[i];

								if (alternate_column_for_main_column_key[event_object_keys_for_column_key[column_index_for_key[i]]] != undefined) {
									const column_key = alternate_column_for_main_column_key[event_object_keys_for_column_key[column_index_for_key[i]]];
									let seconds_value = toSeconds(getSafeString(column_value));
									new_event[column_key] = seconds_value;
								}

								if (column_value.replace(/\s/g, '') == '') {
									continue;
								}

								// EVENT_TYPE has no entry here, it is already handled above to decide Pyro vs DMX,
								// so without this guard it lands as a literal new_event[undefined] property.
								const event_object_key = event_object_keys_for_column_key[column_index_for_key[i]];
								if (event_object_key == undefined) {
									continue;
								}

								if (event_object_key == 'quantity') {
									const trimmed_quantity = column_value.trim();
									const parsed_quantity = parseInt(trimmed_quantity, 10);
									if (isNaN(parsed_quantity) == false && parsed_quantity > 0 && String(parsed_quantity) === trimmed_quantity) {
										new_event.quantity = parsed_quantity;
									}
									continue;
								}

								new_event[event_object_key] = column_value;
							} else {
								//custom columns
								if (custom_column_offset == undefined) {
									custom_column_offset = i;
								}
								if (new_event.custom_values == undefined) {
									new_event.custom_values = {};
								}
								const custom_columns = this.active_show.custom_columns || [];
								new_event.custom_values[custom_columns[i - custom_column_offset]] = columns[i];
							}
						}

						if (new_event.firework_name != undefined && new_event.firework_name != '' && new_event.isDMX != true) {
							for (let i = 0; i < this.copied_events.length; i++) {
								const event = this.copied_events[i];
								if (event.isDMX == true || event.isCOMMENT == true) {
									continue;
								}
								if (event.firework_name?.trim() == new_event.firework_name?.trim()) {
									new_event.my_firework_id = event.my_firework_id;
									new_event.inventory_item_id = event.inventory_item_id;
									break;
								}
							}
						}
						validateEvent(this.active_show, new_event, undefined, undefined, undefined, true);
					}

					if (rows_skipped_for_invalid_event_time > 0) {
						NotificationCenter.add(new NotificationHandler.Notification('Non Number Inputted', `${rows_skipped_for_invalid_event_time} pasted row${rows_skipped_for_invalid_event_time > 1 ? 's' : ''} could not be added because the event time contained a character other than a number.`, [], NotificationType.WARNING));
					}

					this.current_selected_row_index_for_pasting = undefined; // just to set its value again as undefined to not cause any potential issues in getIndexOfEventToBeAdded function.
					this.active_show.events = sortEvents(this.active_show.events);
					this.active_show.show_type = updateShowType(this.active_show.events);

					// A STEP event fires relative to the row above it, so its times can only be calculated once
					// every pasted row is in place and sorted. Runs once for the clipboard rather than per row.
					if (any_step_event_was_pasted == true) {
						calculateEventTimes(this.active_show.events, this.active_show.show_attributes.firmware_version);
					}

					// addNewEvent normally selects and scrolls to the event it added, but a pasted row returns
					// before that, so the last pasted row is selected here once for the whole clipboard.
					if (last_added_event != undefined) {
						if (this.new_event_selection_timeout != undefined && this.new_event_selection_timeout != null) {
							clearTimeout(this.new_event_selection_timeout);
						}

						// have to add some delay so that the event is added and only then the event is selected + scrolled after the events are sorted
						this.new_event_selection_timeout = setTimeout(() => {
							this.selectAnEvent({
								event_id: last_added_event.event_id,
								firework_name: last_added_event.firework_name,
								do_not_seek_audio_playback: true,
							});
							this.scrollToRow(last_added_event.event_id);
						}, 50);
					}

					this.setChangeMadeToTrue();
					this.newly_updated_selected_events = this.active_show_selected_events;
					this.toggle_waveform_reset = !this.toggle_waveform_reset;
					this.action_being_performed = SHOW_METHODS.pasteEvent;

					const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
					// const undo_manager = useUndoManagerStore();
					undo_manager.addAction(post_update_events, original_events, (previous_events) => {
						this.active_show.events = previous_events;
						// useInventoryStore()?.setTriggerFindInventoryChanges(true);
						inventory_store.setTriggerFindInventoryChanges(true);
						this.setChangeMadeToTrue();
					});

					// const end_time = new Date().getTime();
					// Commented out: paste timing instrumentation, kept for future profiling runs.
					// console.log('debugger total time in seconds : ', (end_time - start_time) / 1000);

					// Hide loader after processing is complete
					return;
				}

				//Deal with one or more rows, across one or more columns
				// A single line holding tabs is a row of columns. Commas are not treated as a column split here,
				// because a pasted description can contain one.
				if (this.user_is_editing == true && (lines.length > 1 || (lines[0].includes('\t') == true && this.active_show_selected_events?.length >= 1))) {
					e.preventDefault();

					let affected_events = [];
					// Index into the full events array; still needed further down for validateEvent().
					let index_of_event_id = returnIndexOfEventIds(this.active_show.events);

					// The paste must follow the rows the user can actually see. Walking the full events array
					// lets hidden rows (for example DMX rows in a pyro show) absorb a pasted value that the
					// user believed was going to the next visible row.
					const visible_events = this.filtered_events_based_on_show_type;
					const visible_index_of_event_id = returnIndexOfEventIds(visible_events);
					const starting_index = visible_index_of_event_id[this.active_show_selected_events[0]];

					// Defensive: without this an undefined anchor makes the bounds check NaN, which never breaks
					// the loop and fills affected_events with undefined entries.
					if (starting_index == undefined) {
						return;
					}

					for (let i = 0; i < lines.length; i++) {
						if (starting_index + i >= visible_events.length) {
							break;
						}
						affected_events.push(visible_events[starting_index + i]);
					}

					let focus_row_cell_id = document.activeElement.id;
					if (focus_row_cell_id == undefined || focus_row_cell_id == '') {
						focus_row_cell_id = this.context_menu_opened_on_focused_cell_id;
					}

					// Parse the first line to determine number of columns. Split the same way the rows below
					// are split, or a comma inside a description would claim a column that never gets a value.
					let first_line_columns = lines[0].split('\t');
					let number_of_columns = first_line_columns.length;

					// Resolve the target columns once, from the row the user clicked in. Cell ids are
					// `<column_key>-<event_id>`, so the key stays correct no matter which columns are hidden and
					// never goes stale the way a cached positional index does.
					const focused_cell = document.getElementById(focus_row_cell_id);
					// The event description column puts its contenteditable element in a <div> nested inside the
					// <td>, so anchor on the enclosing cell instead of the focused element itself.
					const focused_table_cell = focused_cell != undefined ? focused_cell.closest('td') : undefined;
					const focused_row_cells = focused_table_cell != undefined ? Array.from(focused_table_cell.closest('tr').querySelectorAll('td')) : [];
					const focused_cell_position = focused_row_cells.indexOf(focused_table_cell);
					if (focused_cell_position < 0) {
						return;
					}

					// These cells hold a structured value (a firing group object, a defined position) that a
					// pasted string can not be turned into, so they take no value.
					const non_pasteable_column_keys = ['disablegroup', 'definedposition'];
					const resolveColumnKeyForCell = (table_cell) => {
						// Prefer a nested editable element's id; that is where the event description column
						// carries its real column key, while the <td> around it is named parent_firework_name.
						const editable_child = table_cell.querySelector('[contenteditable="true"][id]');
						const cell_id = editable_child != null ? editable_child.id : table_cell.id;
						// Comment cells carry no id, index cells are display only, and the event time cells name
						// their wrapper `td_event_time-...` / `td_relative-event_time-...`, which is not a field.
						if (cell_id == undefined || cell_id == '' || cell_id.includes('rowIndex') == true || cell_id.startsWith('td_') == true) {
							return undefined;
						}
						const column_key = cell_id.split('-')[0];
						if (non_pasteable_column_keys.includes(column_key) == true) {
							return undefined;
						}
						return column_key;
					};

					// Columns that can not be pasted into keep their slot as undefined instead of being filtered
					// out, otherwise every column after them would shift left onto the wrong values. Pasting
					// "10 a 2 x" starting on Pulse Time, with Position and a custom column to its right:
					//   10 -> Pulse Time      written
					//   a  -> Position        dropped, a position can not be built from text
					//   2  -> Default Custom  written
					//   x  -> no column left  ignored
					const column_keys_to_paste = focused_row_cells.slice(focused_cell_position, focused_cell_position + number_of_columns).map((table_cell) => resolveColumnKeyForCell(table_cell));
					console.log('[EVS-981_CC_DEBUG] pasteEvent MULTI-ROW PASTE start', {
						focus_row_cell_id,
						cell_key_inferred: focus_row_cell_id?.split('-')?.[0],
						num_lines: lines.length,
						lines_preview: lines.slice(0, 30),
						starting_index,
						selected_events_first: this.active_show_selected_events?.[0],
						affected_event_ids: affected_events.map((ev) => ev?.event_id),
						affected_before: affected_events.map((ev) => ({
							event_id: ev?.event_id,
							channel: ev?.channel,
							cue: ev?.cue,
							cue_auto_incremented: ev?.cue_auto_incremented,
						})),
					});

					for (let i = 0; i < affected_events.length; i++) {
						let event = affected_events[i];

						// A comment row has no pasteable columns. It still consumes its line so that the row on
						// screen keeps lining up with the line the user copied.
						if (event.isCOMMENT == true) {
							continue;
						}

						let event_array_index = index_of_event_id[event.event_id];

						// Parse the current line into columns. Tab only, because a description can contain a
						// comma, and a line without tabs stays one column and lands in the focused cell.
						let line_columns = lines[i].split('\t');

						// Paste data into each column starting from the focused column
						for (let col_index = 0; col_index < column_keys_to_paste.length; col_index++) {
							let cell_value = line_columns[col_index] ? line_columns[col_index].trim() : '';

							// Skip empty values
							if (cell_value === '') {
								continue;
							}

							let cell_key = column_keys_to_paste[col_index];
							// Slot kept for alignment only; this column takes no pasted value.
							if (cell_key == undefined) {
								continue;
							}

							if (cell_key.includes('custom_values')) {
								// fix cell_key for custom_columns
								cell_key = cell_key.replace('custom_values', '');
								cell_key = cell_key.replaceAll('fill_space', ' ');
								if (event.custom_values == undefined) {
									event.custom_values = {};
								}
								event.custom_values[cell_key] = cell_value.trim();
							} else {
								switch (cell_key) {
									case 'duration_in_seconds':
										this.updateEventDuration(event, shortenTime(cell_value.trim())?.replace('s', ''));
										break;
									case 'igniter_pre_fire_in_seconds':
										this.updateEventPreFire(event, shortenTime(cell_value.trim())?.replace('s', ''));
										break;
									case 'lift_time_in_seconds':
										this.updateEventLiftTime(event, shortenTime(cell_value.trim())?.replace('s', ''));
										break;
									case 'pulse':
										this.updateEventPulseTime(event, toSmallestDisplayTime(cell_value.trim(), event.isDMX == true ? EVENT_TYPES.DMX : EVENT_TYPES.PYRO)?.replace('s', ''));
										break;
									case 'quantity': {
										const trimmed_quantity = cell_value.trim();
										if (trimmed_quantity == '') {
											this.updateEventQuantity(event, undefined);
										} else {
											const parsed_quantity = parseInt(trimmed_quantity, 10);
											if (isNaN(parsed_quantity) == false && parsed_quantity > 0 && String(parsed_quantity) === trimmed_quantity) {
												this.updateEventQuantity(event, parsed_quantity);
											}
										}
										break;
									}
									default:
										event[cell_key] = cell_value.trim();
								}
							}

							if (cell_key == 'channel') {
								const settings_store = useSettingStore();

								if (settings_store?.settings?.auto_increment_cues === true && (event?.cue_auto_incremented == undefined || event.cue_auto_incremented == true) && event.isDMX !== true) {
									updateCuesUsedPerChannel(this.active_show.events);
									const cue_for_channel = getNextCueForChannel(cell_value.trim());
									this.updateEventCue(event, cue_for_channel, true);
									//TODO: check if needed
									validateEvent(this.active_show, event, event_array_index, undefined, undefined, true);
								}
							}

							if (cell_key == 'igniter_pre_fire_in_seconds' || cell_key == 'lift_time_in_seconds') {
								event.negative_calculated_event_time = calculateEventTimeForEvent(event, this.active_show.firmware_version).negative_calculated_time;
							}

							if (cell_key == 'cue') {
								event.cue_auto_incremented = false;
							}
						}

						// Validate once per row after all of its pasted columns are written, rather than once per
						// column - the channel column's own validateEvent call above (which also updates used
						// cues) still runs inline, since it depends on that column's auto-increment branch.
						validateEvent(this.active_show, event, event_array_index);
					}

					console.log('[EVS-981_CC_DEBUG] pasteEvent MULTI-ROW PASTE end', {
						affected_after: affected_events.map((ev) => ({
							event_id: ev?.event_id,
							channel: ev?.channel,
							cue: ev?.cue,
							cue_auto_incremented: ev?.cue_auto_incremented,
						})),
					});

					if (affected_events.length > 0) {
						this.setChangeMadeToTrue();
						this.newly_updated_selected_events = [...affected_events];
						this.toggle_waveform_reset = !this.toggle_waveform_reset;
						this.action_being_performed = SHOW_METHODS.pasteEvent;
					}

					const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
					const undo_manager = useUndoManagerStore();
					undo_manager.addAction(post_update_events, original_events, (previous_events) => {
						this.active_show.events = previous_events;
						this.setChangeMadeToTrue();
					});
					return;
				}

				//Deal with single row and single column
				if (this.user_is_editing == true && lines.length === 1) {
					// document.execCommand("insertText", false, text); this is no longer working, probably because its depreciated.

					e.preventDefault();
					let focus_row_cell_id = document.activeElement.id;
					if (focus_row_cell_id == undefined || focus_row_cell_id == '') {
						focus_row_cell_id = this.context_menu_opened_on_focused_cell_id;
					}

					this.should_highlight_cell_text = false;
					document.getElementById(focus_row_cell_id).focus();
					console.log(' insert text : ', text, document.activeElement);
					// Save the current selection
					const selection = window.getSelection();
					let range;

					if (from_shortcut === true) {
						range = selection.getRangeAt(0);
					} else {
						range = this.context_menu_opened_at_range;
					}
					// Replace the selected text with the new text
					range.deleteContents();
					range.insertNode(document.createTextNode(text));
					this.should_highlight_cell_text = true;

					const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
					const undo_manager = useUndoManagerStore();
					undo_manager.addAction(post_update_events, original_events, (previous_events) => {
						this.active_show.events = previous_events;
						this.setChangeMadeToTrue();
					});

					return;
				}
			} finally {
				loader_store.toggleEventTableLoader(false);
			}
		},
		deleteEvent() {
			if (this.is_show_locked == true) {
				return;
			}

			if (this.active_show_selected_events?.length > 0) {
				const setting_store = useSettingStore();
				if (setting_store.settings && setting_store.settings.should_ask_before_deleting_cues == true) {
					let cancel_button = new Modal.Button('No', undefined, { inverted: true, border_inverted: true, bigger: true });
					let confirm_button = new Modal.Button(
						'Yes, Delete',
						async () => {
							//On click
							this.onConfirmDeleteEvents();
						},
						{ destructive: true, bigger: true, dismiss_modal_after_click: true, primary_action_button: true }
					);
					let events_plural = this.active_show_selected_events?.length == 1 ? 'Event' : 'Events';
					Modal.createInteractiveModal(`Delete Selected ${events_plural}?`, `Are you sure you want to delete the currently selected ${events_plural.toLowerCase()}?`, null, [cancel_button, confirm_button], { destroy_on_dismissal: true });
				} else {
					this.onConfirmDeleteEvents();
				}
			}
		},
		insertEventAboveOrBelow(position, selected_event_index = undefined, dropped_to_waveform_event_time = undefined, add_undo_redo = true) {
			if (this.is_show_locked == true) {
				return;
			}

			const index_of_selected_event = selected_event_index != undefined ? selected_event_index : this.active_show.events.findIndex((event) => event.event_id == this.active_show_selected_events[0]);
			if (index_of_selected_event < 0) {
				NotificationCenter.add(new NotificationHandler.Notification('Error', 'Invalid selected event.', null, NotificationType.ERROR));
				return;
			}

			const is_firework_or_dmx_being_dragged = this.currently_dragged_firework != undefined || this.currently_dragged_dmx != undefined;
			if ((this.active_show.events[index_of_selected_event]?.isALTERNATE1 || this.active_show.events[index_of_selected_event]?.isALTERNATE2) && is_firework_or_dmx_being_dragged == true) {
				NotificationCenter.add(new NotificationHandler.Notification('Not Allowed', 'Can not drop above or below an alternate event.', null, NotificationType.ERROR));
				// to allow add alternate event above/below
				this.currently_dragged_dmx = null;
				this.currently_dragged_firework = null;
				return;
			}

			let original_events = JSON.parse(JSON.stringify(this.active_show.events));

			let events_before_adding_new_event = [...this.active_show.events];
			let updated_events_array = [];
			let new_event_id = undefined;

			if (position === 'above') {
				const { events, added_event_id } = insertTimedEventAbove(this.active_show.events, this.active_show.events[index_of_selected_event], this.active_show.show_attributes?.show_type, useWaveformStore()?.selected_add_event_state, this.active_show_selected_events, useWaveformStore()?.current_audio_playback_time, this.currently_dragged_dmx != undefined ? ADD_EVENT_BUTTON_STATES.DMX : null);

				updated_events_array = events;
				new_event_id = added_event_id;
			} else {
				const { events, added_event_id } = insertTimedEventBelow(
					this.active_show.events,
					this.active_show.events[index_of_selected_event],
					this.active_show.show_attributes?.show_type,
					useWaveformStore()?.selected_add_event_state,
					this.active_show_selected_events,
					useWaveformStore()?.current_audio_playback_time,
					this.currently_dragged_dmx != undefined ? ADD_EVENT_BUTTON_STATES.DMX : null,
					dropped_to_waveform_event_time
				);
				updated_events_array = events;
				new_event_id = added_event_id;
			}

			this.active_show.events = updated_events_array;

			this.newly_updated_selected_events = updated_events_array.filter(
				(event1) =>
					!events_before_adding_new_event.some((event2) => {
						return event2.event_id === event1.event_id;
					})
			);
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.insertEventAboveOrBelow;
			const current_event_being_updated = this.active_show.events.find((event) => event.event_id == new_event_id);
			let parent_step = undefined;
			let adjusted_times = undefined;
			if (current_event_being_updated.parent_event != undefined) {
				parent_step = this.active_show.events.find((event) => event.event_id == current_event_being_updated.parent_event);
				adjusted_times = calculateEventTimeForEvent(current_event_being_updated, this.active_show.firmware_version, parent_step?.timeline_event_time);
			} else {
				adjusted_times = calculateEventTimeForEvent(current_event_being_updated, this.active_show.firmware_version);
			}

			updateRelativeAndCalculatedTimesForAEvent({ event: current_event_being_updated, adjusted_times });

			this.setChangeMadeToTrue();

			if (add_undo_redo) {
				const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
				const undo_manager = useUndoManagerStore();
				undo_manager.addAction(post_update_events, original_events, (previous_events) => {
					this.active_show.events = previous_events;
					this.setChangeMadeToTrue();
				});
			}

			return new_event_id;
		},
		makeComment() {
			if (this.is_show_locked == true) {
				return;
			}

			let original_events = JSON.parse(JSON.stringify(this.active_show.events));

			// duration/pulse must be cleared so the resulting comment doesn't inherit the source event's waveform pill width.
			// pulse is set to undefined (not 0) to match updateEventPulseTime's "no pulse" convention from EVS-1046.
			this.active_show.events = this.active_show.events.map((event) => ({
				...event,
				isCOMMENT: this.active_show_selected_events.includes(event.event_id) ? true : event.isCOMMENT,
				duration: this.active_show_selected_events.includes(event.event_id) ? '0s' : event.duration,
				duration_in_seconds: this.active_show_selected_events.includes(event.event_id) ? 0 : event.duration_in_seconds,
				pulse: this.active_show_selected_events.includes(event.event_id) ? undefined : event.pulse,
				igniter_pre_fire: this.active_show_selected_events.includes(event.event_id) ? '0' : event.igniter_pre_fire,
				igniter_pre_fire_in_seconds: this.active_show_selected_events.includes(event.event_id) ? 0 : event.igniter_pre_fire_in_seconds,
				lift_time: this.active_show_selected_events.includes(event.event_id) ? '0' : event.lift_time,
				lift_time_in_seconds: this.active_show_selected_events.includes(event.event_id) ? 0 : event.lift_time_in_seconds,
				isSTEP: this.active_show_selected_events.includes(event.event_id) ? false : event.isSTEP,
				isALTERNATE1: this.active_show_selected_events.includes(event.event_id) ? false : event.isALTERNATE1,
				isALTERNATE2: this.active_show_selected_events.includes(event.event_id) ? false : event.isALTERNATE2,
			}));
			this.active_show.show_type = updateShowType(this.active_show.events);

			this.setChangeMadeToTrue();

			this.newly_updated_selected_events = this.active_show.events.filter((event) => this.active_show_selected_events.includes(event.event_id));
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.makeComment;

			this.unselectRows(); // to show the green color of comment, instead of selected row color i.e blue

			const undo_manager = useUndoManagerStore();
			const affected_events = JSON.parse(JSON.stringify(this.newly_updated_selected_events));

			undo_manager.addAction(affected_events, original_events);
		},
		duplicateEvent() {
			if (this.is_show_locked == true) {
				return;
			}

			if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false) {
				if (this.active_show.events.length >= MAX_NUMBER_OF_FREE_TIER_EVENTS) {
					NotificationCenter.add(new NotificationHandler.Notification('Free Tier Limit', `You have reached the maximum number of events allowed on your trial plan.`, [], NotificationType.ERROR, { has_close_button: true }));
					return;
				}
			}

			let original_events = JSON.parse(JSON.stringify(this.active_show.events));
			let events_before_adding_new_event = [...this.active_show.events];
			const selected_events = this.active_show?.events?.filter((event) => this.active_show_selected_events?.includes(event.event_id));
			for (let selected_event of selected_events) {
				// Create a deep copy of the selected event
				const duplicate_event = JSON.parse(JSON.stringify({ ...selected_event, event_id: createUniqueID() }));
				const index_of_selected_event = this.active_show.events.findIndex((event) => event.event_id == selected_event.event_id);
				if (index_of_selected_event < 0) {
					return;
				}
				let index_position_to_add_event = index_of_selected_event + 1;
				this.active_show.events.splice(index_position_to_add_event, 0, duplicate_event);
				if (duplicate_event.isSTEP == true) {
					// as per new requirement i.e evs-318 : the newly added step should always be snapped/attached to the previous timed or step event
					// we can use filter as at this point all the events should always be sorted w.r.t timeline_event_time
					let { previous_event_time, previous_event_time_in_seconds, previous_event_duration_in_seconds } = getPreviousEventDetails(original_events, index_of_selected_event + 1);
					if (previous_event_duration_in_seconds > 0) {
						previous_event_time_in_seconds = previous_event_time_in_seconds + previous_event_duration_in_seconds;
						previous_event_time = formatDisplayableTime(previous_event_time_in_seconds);
					}

					let step_index = index_of_selected_event + 1;
					this.active_show.events[step_index].isSTEP = true;
					this.active_show.events[step_index].timeline_event_time = previous_event_time_in_seconds;
					this.active_show.events[step_index].event_time_in_seconds = previous_event_time_in_seconds;
					this.active_show.events[step_index].event_time = previous_event_time;
					// events = updateTimelineEventTime(events); //I don't think this was implemented right. It seems like it is designed to fix a specific bug but it is called every time a STEP is added.
					createStepParentChildRelationship({ start_from_row_index: index_of_selected_event });
				}
				this.setChangeMadeToTrue();
			}

			this.newly_updated_selected_events = this.active_show?.events.filter(
				(event1) =>
					!events_before_adding_new_event.some((event2) => {
						return event2.event_id === event1.event_id;
					})
			);
			this.active_show.show_type = updateShowType(this.active_show.events);
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.duplicateEvent;

			useInventoryStore()?.setTriggerFindInventoryChanges();

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setChangeMadeToTrue();
			});
		},

		cellValidation(column_type, errors, validation_type = 'error') {
			if (errors == undefined || errors.length < 1) {
				return;
			}

			if (validation_type == 'warning') {
				const column_warning = errors.find((warning) => warning.column == column_type);
				if (column_warning != undefined || column_warning != null) {
					this.cell_error = column_warning;
					this.cell_error.warning = true;
				}
				return;
			}
			if (validation_type == 'error') {
				const column_error = errors.find((error) => error.column == column_type);
				if (column_error != undefined || column_error != null) {
					this.cell_error = column_error;
				}
				return;
			}
		},
		removeCellValidation() {
			this.cell_error = undefined;
		},
		setSelectedEventMethod(method, event_id) {
			this.event_edit_attributes.selected_event_method = method;

			if (event_id != undefined) {
				// event_id is not passed from keyboard shortcuts, waveform but the handleFocus function in EditEventModal.vue handles this scenario and recalls setSelectedEventMethod function as provides event_id, hence updateNewTime is called.
				this.updateNewTime(event_id);
			}
		},
		setNewTime(value, event_id, split_time_values, time_element, options = {}) {
			if (typeof value === 'string' && value.startsWith('.') === true) {
				value = '0' + value;
			}

			if (options.is_relative_time == true) {
				if (time_element == 'minute') {
					this.event_edit_attributes.relative_min_input_value = value;
					this.event_edit_attributes.relative_sec_input_value = this.event_edit_attributes.relative_sec_input_value ?? split_time_values.sec;
					this.event_edit_attributes.relative_mil_sec_input_value = this.event_edit_attributes.relative_mil_sec_input_value ?? split_time_values.milSec;
				} else if (time_element == 'second') {
					this.event_edit_attributes.relative_min_input_value = this.event_edit_attributes.relative_min_input_value ?? split_time_values.min;
					this.event_edit_attributes.relative_sec_input_value = value;
					this.event_edit_attributes.relative_mil_sec_input_value = this.event_edit_attributes.relative_mil_sec_input_value ?? split_time_values.milSec;
				} else if (time_element == 'milli_sec') {
					this.event_edit_attributes.relative_min_input_value = this.event_edit_attributes.relative_min_input_value ?? split_time_values.min;
					this.event_edit_attributes.relative_sec_input_value = this.event_edit_attributes.relative_sec_input_value ?? split_time_values.sec;
					this.event_edit_attributes.relative_mil_sec_input_value = value;
				}

				let new_relative_time = this.event_edit_attributes.relative_min_input_value + ':' + this.event_edit_attributes.relative_sec_input_value + '.' + this.event_edit_attributes.relative_mil_sec_input_value;
				let new_relative_time_in_seconds = convertFormattedStringIntoSeconds(new_relative_time);
				let new_relative_time_formatted = formatDisplayableTime('' + new_relative_time_in_seconds);

				this.event_edit_attributes.new_relative_time = new_relative_time_formatted;

				return;
			}

			if (time_element == 'minute') {
				this.event_edit_attributes.min_input_value = value;
				this.event_edit_attributes.sec_input_value = this.event_edit_attributes.sec_input_value ?? split_time_values.sec;
				this.event_edit_attributes.mil_sec_input_value = this.event_edit_attributes.mil_sec_input_value ?? split_time_values.milSec;
			} else if (time_element == 'second') {
				this.event_edit_attributes.min_input_value = this.event_edit_attributes.min_input_value ?? split_time_values.min;
				this.event_edit_attributes.sec_input_value = value;
				this.event_edit_attributes.mil_sec_input_value = this.event_edit_attributes.mil_sec_input_value ?? split_time_values.milSec;
			} else if (time_element == 'milli_sec') {
				this.event_edit_attributes.min_input_value = this.event_edit_attributes.min_input_value ?? split_time_values.min;
				this.event_edit_attributes.sec_input_value = this.event_edit_attributes.sec_input_value ?? split_time_values.sec;
				this.event_edit_attributes.mil_sec_input_value = value;
			}

			this.event_edit_attributes.new_time = calculateEventTimeWithMethod({
				selected_method: this.event_edit_attributes.selected_event_method,
				event_id,
				events: this.active_show.events,
				min_input_value: this.event_edit_attributes.min_input_value,
				sec_input_value: this.event_edit_attributes.sec_input_value,
				mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value,
				audio_duration: useWaveformStore()?.audio_duration,
				delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
			})?.event_time;
		},
		setDelaySecondsValue(value, event_id) {
			this.event_edit_attributes.delay_sec_input_value = value;
			this.event_edit_attributes.new_time = calculateEventTimeWithMethod({
				selected_method: this.event_edit_attributes.selected_event_method,
				event_id,
				events: this.active_show.events,
				min_input_value: this.event_edit_attributes.min_input_value,
				sec_input_value: this.event_edit_attributes.sec_input_value,
				mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value,
				audio_duration: useWaveformStore()?.audio_duration,
				delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
			})?.event_time;
		},
		setShiftSecondsValue(value, event_id) {
			this.event_edit_attributes.sec_input_value = value;
			this.event_edit_attributes.new_time = calculateEventTimeWithMethod(this.event_edit_attributes.selected_event_method, event_id, this.active_show.events, this.event_edit_attributes.min_input_value, this.event_edit_attributes.sec_input_value, this.event_edit_attributes.mil_sec_input_value, useWaveformStore()?.audio_duration, this.info_about_loaded_song?.file_url)?.event_time;
		},
		updateNewTime(event_id) {
			this.event_edit_attributes.new_time = calculateEventTimeWithMethod({
				selected_method: this.event_edit_attributes.selected_event_method,
				event_id,
				events: this.active_show.events,
				min_input_value: this.event_edit_attributes.min_input_value,
				sec_input_value: this.event_edit_attributes.sec_input_value,
				mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value,
				audio_duration: useWaveformStore()?.audio_duration,
				delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
			})?.event_time;
		},
		addNewEvent({ selected_add_event_state = undefined, dropped_event_time = undefined, add_undo_redo = true, from_paste = false } = {}) {
			const waveform_store = useWaveformStore();
			// Commented out: debug tracing, fires once per event added during a paste.
			// console.log('[EVS-711-LOG] 26');
			if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false && this.active_show.events.length >= MAX_NUMBER_OF_FREE_TIER_EVENTS) {
				return;
			}

			if (this.is_show_locked == true) {
				return;
			}
			if (selected_add_event_state == undefined) {
				selected_add_event_state = waveform_store?.selected_add_event_state;
			}
			if (this.active_show?.events == undefined) {
				return;
			}
			// console.log('[EVS-711-LOG] 29');

			let original_events;
			if (add_undo_redo) {
				original_events = JSON.parse(JSON.stringify(this.active_show.events));
			}

			// console.log('[EVS-711-LOG] 30');
			let leave_time_blank = false;
			let time = dropped_event_time;
			let make_step_event = false;
			let make_dmx_event = false;
			let number_of_events_to_add = waveform_store?.number_of_events_to_insert;
			let show_type = this.active_show?.show_attributes?.show_type;

			let current_audio_playback_time = waveform_store?.current_audio_playback_time || 0;

			const events_before_adding_new_event = [...this.active_show.events];
			let updated_events_array = [];

			const new_event_object = {
				show_type,
				current_wave_time: current_audio_playback_time,
				events: this.active_show.events,
				selected_rows: this.active_show_selected_events,
				time,
				leave_time_blank,
				make_STEP_event: make_step_event,
				make_DMX_event: make_dmx_event,
				number_of_events_to_add,
			};

			let result = undefined;

			let new_event = undefined;

			switch (selected_add_event_state) {
				case ADD_EVENT_BUTTON_STATES.STEP: {
					new_event_object.make_STEP_event = true;
					result = addNewEventToTable(new_event_object);
					if (result == undefined) {
						return;
					}
					updated_events_array = result.updated_events;
					new_event = result.new_event;
					break;
				}
				case ADD_EVENT_BUTTON_STATES.DMX:
					new_event_object.make_DMX_event = true;
					result = addNewEventToTable(new_event_object);
					if (result == undefined) {
						return;
					}
					updated_events_array = result.updated_events;
					new_event = result.new_event;
					break;
				default:
					result = addNewEventToTable(new_event_object);
					if (result == undefined) {
						return;
					}
					updated_events_array = result.updated_events;
					new_event = result.new_event;
					break;
			}
			// This sorted the whole show once per added event, so a large paste sorted it again on every
			// pasted row. addNewEventToTable splices each new event in at its time ordered position and
			// mutates this.active_show.events directly, so the array stays sorted without it.
			// this.active_show.events = sortEvents(updated_events_array);

			// A paste sorts and updates the show type once for the whole clipboard, after its loop in
			// pasteEvent, so doing it per pasted row is wasted work.
			if (from_paste == false) {
				this.active_show.show_type = updateShowType(this.active_show.events);
				this.active_show.events = sortEvents(updated_events_array);
			}

			if (new_event != undefined) {
				// Everything below this point is redone for the whole clipboard once pasteEvent's loop
				// finishes, so a pasted row returns here rather than repeating it per row.
				if (from_paste == true) {
					return new_event;
				}

				// Clear any existing timeout
				if (this.new_event_selection_timeout != undefined && this.new_event_selection_timeout != null) {
					clearTimeout(this.new_event_selection_timeout);
				}

				// have to add some delay so that the event is added and only then the event is selected + scrolled after the events are sorted
				this.new_event_selection_timeout = setTimeout(() => {
					this.selectAnEvent({
						event_id: new_event.event_id,
						firework_name: new_event.firework_name,
						do_not_seek_audio_playback: true,
					});
					this.scrollToRow(new_event.event_id);
				}, 50);
			}
			this.setChangeMadeToTrue();
			if (selected_add_event_state == ADD_EVENT_BUTTON_STATES.STEP) {
				calculateEventTimes(updated_events_array, this.active_show.show_attributes.firmware_version);
			}
			this.newly_updated_selected_events = updated_events_array.filter(
				(event1) =>
					!events_before_adding_new_event.some((event2) => {
						return event2.event_id === event1.event_id;
					})
			);
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.addNewEvent;
			if (add_undo_redo) {
				const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
				const undo_manager = useUndoManagerStore();
				undo_manager.addAction(post_update_events, original_events, (previous_events) => {
					this.active_show.events = previous_events;
					this.setChangeMadeToTrue();
				});
			}
		},
		setSelectedColumnCell(column_cell) {
			this.active_show_selected_column_cell = column_cell;
		},
		insertFireworkIntoRow(firework, event_id = null, { add_undo_redo = true } = {}) {
			if (this.is_show_locked == true || firework == undefined) {
				return;
			}

			// TODO: Handle error
			if (this.active_show.show_attributes.show_type === SHOW_TYPES.DMX.key) {
				return { error: 'non pyro show' };
			}

			let original_events = add_undo_redo == true ? JSON.parse(JSON.stringify(this.active_show.events)) : undefined;

			if (event_id != null) {
				this.active_show_selected_events = [event_id];
			}

			if (this.active_show_selected_events.length > 0) {
				let selected_rows_array_indexes = [];
				let index_of_event_id = returnIndexOfEventIds(this.active_show.events);
				for (let i = 0; i < this.active_show_selected_events.length; i++) {
					const event = this.active_show.events.find((event) => {
						return event.event_id == this.active_show_selected_events[i];
					});

					// if event is not a dmx event i.e. its a firework event add its index to selected_rows
					if (event.isDMX != true) {
						selected_rows_array_indexes.push(index_of_event_id[this.active_show_selected_events[i]]);
					}
				}

				//Add firework to currently drawn table
				if (selected_rows_array_indexes.length > 0) {
					this.updateShowForInsertedFirework(firework, selected_rows_array_indexes, true);
				}

				this.newly_updated_selected_events = this.active_show.events.filter((event) => this.active_show_selected_events.includes(event.event_id) === true);
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.insertEventAboveOrBelow;

				this.setChangeMadeToTrue();
				useInventoryStore()?.setTriggerFindInventoryChanges();

				// EVS-1147: when called from addFireworkToEvent, parent owns the single undo entry
				if (add_undo_redo == true && original_events != undefined) {
					const undo_manager = useUndoManagerStore();
					const affected_events = JSON.parse(JSON.stringify(this.newly_updated_selected_events));
					undo_manager.addAction(affected_events, original_events);
				}
			}
		},
		insertDmxIntoRow(dmx, event_id = null, { add_undo_redo = true } = {}) {
			if (this.is_show_locked == true || dmx == undefined) {
				return;
			}
			// TODO: Handle error
			if (this.active_show.show_attributes.show_type === SHOW_TYPES.PYRO.key) {
				return { error: 'non dmx show' };
			}

			let original_events = add_undo_redo == true ? JSON.parse(JSON.stringify(this.active_show.events)) : undefined;

			if (event_id != null) {
				this.active_show_selected_events = [event_id];
			}

			if (this.active_show_selected_events.length > 0) {
				let selected_rows_array_indexes = [];
				let index_of_event_id = returnIndexOfEventIds(this.active_show.events);
				for (let i = 0; i < this.active_show_selected_events.length; i++) {
					const event = this.active_show.events.find((event) => event.event_id == this.active_show_selected_events[i]);
					if (event.isDMX == true) {
						selected_rows_array_indexes.push(index_of_event_id[this.active_show_selected_events[i]]);
					}
				}
				//Add dmx to currently drawn table
				if (selected_rows_array_indexes.length > 0) {
					this.updateShowForInsertedDmx(dmx, selected_rows_array_indexes, true);
				}

				this.newly_updated_selected_events = this.active_show.events.filter((event) => this.active_show_selected_events.includes(event.event_id) === true);
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.insertEventAboveOrBelow;

				this.setChangeMadeToTrue();

				// EVS-1147: when called from addDMXToEvent, parent owns the single undo entry
				if (add_undo_redo == true && original_events != undefined) {
					const undo_manager = useUndoManagerStore();
					const affected_events = JSON.parse(JSON.stringify(this.newly_updated_selected_events));
					undo_manager.addAction(affected_events, original_events);
				}
			}
		},
		setCurrently_dragged_firework(firework) {
			this.currently_dragged_firework = firework;
			this.currently_dragged_dmx = null;
		},
		setCurrently_dragged_dmx(dmx) {
			this.currently_dragged_dmx = dmx;
			this.currently_dragged_firework = null;
		},
		// for find & replace
		updateShowForInsertedDmx(dmx, selected_rows_indexes, being_called_from_insert_firework_to_row_method = false) {
			if (this.is_show_locked == true) {
				return;
			}

			if (this.active_show != undefined) {
				for (let i = 0; i < selected_rows_indexes.length; i++) {
					let event_index = selected_rows_indexes[i];
					this.active_show.events[event_index].my_dmx_effect_id = dmx.id;
					if (dmx.name != undefined) {
						this.active_show.events[event_index].firework_name = dmx.name;
					}
					if (dmx.duration != undefined) {
						this.active_show.events[event_index].duration = shortenTime(dmx.duration);
						this.active_show.events[event_index].duration_in_seconds = toSeconds(dmx.duration);
					}
					if (dmx.universe != undefined) {
						this.active_show.events[event_index].universe = dmx.universe;
					}
					if (dmx.channel != undefined) {
						this.active_show.events[event_index].channel = dmx.channel;
					}
					if (dmx.value != undefined) {
						this.active_show.events[event_index].cue = dmx.value;
					}
					if (dmx.pulse != undefined && dmx.pulse.trim() != '' && dmx.pulse != 0) {
						this.active_show.events[event_index].pulse = dmx.pulse;
					}

					if (event_index > -1) {
						validateEvent(this.active_show, this.active_show.events[event_index], event_index);
					}
				}

				if (being_called_from_insert_firework_to_row_method === true) {
					return;
				}

				this.setChangeMadeToTrue();

				this.newly_updated_selected_events = this.active_show.events.filter((event) => this.active_show_selected_events.includes(event.event_id) === true);
				this.toggle_waveform_reset = !this.toggle_waveform_reset;
				this.action_being_performed = SHOW_METHODS.updateShowForInsertedDmx;
			}
		},
		// for find & replace
		updateShowForInsertedFirework(firework, selected_rows_indexes, being_called_from_insert_firework_to_row_method = false) {
			if (this.is_show_locked == true) {
				return;
			}

			if (this.active_show != undefined) {
				for (let i = 0; i < selected_rows_indexes.length; i++) {
					let event_index = selected_rows_indexes[i];
					this.active_show.events[event_index].my_firework_id = firework.firework_ID;
					this.active_show.events[event_index].inventory_item_id = firework.inventory_item_id;
					if (firework.name != undefined) {
						this.active_show.events[event_index].firework_name = firework.name;
					}
					if (firework.duration != undefined) {
						this.active_show.events[event_index].duration = shortenTime(firework.duration);
						this.active_show.events[event_index].duration_in_seconds = toSeconds(firework.duration);
					} else {
						this.active_show.events[event_index].duration = undefined;
						this.active_show.events[event_index].duration_in_seconds = undefined;
					}
					if (firework.lift_time != undefined) {
						this.active_show.events[event_index].lift_time_in_seconds = toSeconds(firework.lift_time);
						this.active_show.events[event_index].lift_time = shortenTime(firework.lift_time);
					} else {
						this.active_show.events[event_index].lift_time_in_seconds = undefined;
						this.active_show.events[event_index].lift_time = undefined;
					}
					this.active_show.events[event_index].negative_calculated_event_time = calculateEventTimeForEvent(this.active_show.events[event_index], this.active_show.firmware_version).negative_calculated_time;
					if (firework.custom_fields != undefined) {
						const custom_fields_to_insert = this.active_show.custom_columns.map((custom_column) => {
							const found_match = firework.custom_fields.find((firework_custom_field) => firework_custom_field.name === custom_column);
							return found_match ? { custom_column: custom_column, value: found_match.value } : null;
						});

						// logic to auto insert custom fields from firework data to custom column values in the event
						for (const custom_field of custom_fields_to_insert) {
							if (custom_field != undefined) {
								if (this.active_show.events[event_index].custom_values == undefined) {
									this.active_show.events[event_index].custom_values = {};
								}
								this.active_show.events[event_index].custom_values[custom_field.custom_column] = custom_field.value;
							}
						}
					}

					if (event_index > -1) {
						validateEvent(this.active_show, this.active_show.events[event_index], event_index);
					}
				}

				if (being_called_from_insert_firework_to_row_method === true) {
					return;
				}

				this.setChangeMadeToTrue();
			}
		},
		updateRowTextForInsertedDMXEffect(dmx_effect, selected_rows_indexes) {
			if (this.is_show_locked == true) {
				return;
			}

			if (this.active_show != undefined) {
				for (var i = 0; i < selected_rows_indexes.length; i++) {
					var event_index = selected_rows_indexes[i];
					if (dmx_effect.name != undefined) {
						this.active_show.events[event_index].firework_name = dmx_effect.name;
					}

					if (dmx_effect.universe != undefined) {
						this.active_show.events[event_index].universe = dmx_effect.universe;
					}

					if (dmx_effect.channel != undefined) {
						this.active_show.events[event_index].channel = dmx_effect.channel;
					}

					if (dmx_effect.value != undefined) {
						this.active_show.events[event_index].cue = dmx_effect.value;
					}

					if (dmx_effect.pulse != undefined) {
						this.active_show.events[event_index].pulse = dmx_effect.pulse;
					}

					if (dmx_effect.duration != undefined) {
						this.active_show.events[event_index].duration = dmx_effect.duration;
					}

					if (dmx_effect.lift_time != undefined) {
						this.active_show.events[event_index].lift_time = dmx_effect.lift_time;
					}
				}
			}
		},
		addFireworkToEvent(event, dropped_to_waveform_event_time = undefined) {
			if (this.is_show_locked == true) {
				return;
			}

			let original_events = JSON.parse(JSON.stringify(this.active_show.events));

			let is_above = this.placeholder_row_object.active_show_placeholder_position == 'above';
			let is_below = this.placeholder_row_object.active_show_placeholder_position == 'below';

			// This is the timeline_event_time of the dragged and dropped event to waveform
			if (dropped_to_waveform_event_time != undefined) {
				is_below = true;
				is_above = false;
			}

			if (event != undefined && (is_above == true || is_below == true)) {
				const index_of_selected_event = this.active_show.events.findIndex((ev) => ev.event_id == event.event_id);
				let index_position_to_add_event = 0;
				if (is_above == true) {
					index_position_to_add_event = index_of_selected_event;
					if (index_position_to_add_event < 0) {
						index_position_to_add_event = 0;
					}
				} else {
					index_position_to_add_event = index_of_selected_event + 1;
				}
				let new_event_id = undefined;
				if (is_above !== false) {
					new_event_id = this.insertEventAboveOrBelow('above', index_of_selected_event, undefined, false);
				} else {
					new_event_id = this.insertEventAboveOrBelow('below', index_of_selected_event, dropped_to_waveform_event_time, false);
				}
				if (new_event_id != undefined) {
					this.insertFireworkIntoRow(this.currently_dragged_firework, new_event_id, { add_undo_redo: false });
					this.active_show.events = sortEvents(this.active_show.events);
				}
			} else {
				// firework is dopped on existing event row
				this.insertFireworkIntoRow(this.currently_dragged_firework, event?.event_id, { add_undo_redo: false });
				this.active_show.events = sortEvents(this.active_show.events);
			}

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setChangeMadeToTrue();
			});
		},
		addDMXToEvent(event, dropped_to_waveform_event_time = undefined) {
			if (this.is_show_locked == true) {
				return;
			}
			let original_events = JSON.parse(JSON.stringify(this.active_show.events));

			let is_above = this.placeholder_row_object.active_show_placeholder_position == 'above';
			let is_below = this.placeholder_row_object.active_show_placeholder_position == 'below';

			// This is the timeline_event_time of the dragged and dropped event to waveform
			if (dropped_to_waveform_event_time != undefined) {
				is_below = true;
				is_above = false;
			}

			if (event != undefined && (is_above == true || is_below == true)) {
				const index_of_selected_event = this.active_show.events.findIndex((ev) => ev.event_id == event.event_id);
				let index_position_to_add_event = 0;
				if (is_above == true) {
					index_position_to_add_event = index_of_selected_event;
					if (index_position_to_add_event < 0) {
						index_position_to_add_event = 0;
					}
				} else {
					index_position_to_add_event = index_of_selected_event + 1;
				}

				let new_event_id = undefined;
				if (is_above !== false) {
					new_event_id = this.insertEventAboveOrBelow('above', index_of_selected_event, undefined, false);
				} else {
					new_event_id = this.insertEventAboveOrBelow('below', index_of_selected_event, dropped_to_waveform_event_time, false);
					this.active_show.events = sortEvents(this.active_show.events);
				}
				if (new_event_id != undefined) {
					this.insertDmxIntoRow(this.currently_dragged_dmx, new_event_id, { add_undo_redo: false });
					this.active_show.events = sortEvents(this.active_show.events);
				}
			} else {
				// dmx is dropped on existing event row
				this.insertDmxIntoRow(this.currently_dragged_dmx, event?.event_id, { add_undo_redo: false });
			}

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setChangeMadeToTrue();
			});
		},
		getEventIndex(event_id) {
			let i = undefined;
			this.active_show?.events.forEach(function (x, index) {
				if (x.event_id === event_id) {
					i = index;
				}
			});
			return i;
		},
		showActiveShowPlaceholderRow(placeholder_position, event, next_or_previous_event_id) {
			// const next_or_previous_event = this.active_show.events.find((event) => event.event_id == next_or_previous_event_id);
			this.placeholder_row_object = {
				show_active_show_placeholder_row: true,
				active_show_placeholder_position: placeholder_position,
				placeholder_row_event_id: event?.event_id,
				next_or_previous_event_id,
				next_or_previous_event_time: event?.event_time,
				currently_dragged_firework: this.currently_dragged_firework,
			};
		},
		clearActiveShowPlaceholderRows() {
			this.placeholder_row_object = {
				show_active_show_placeholder_row: false,
				active_show_placeholder_position: null,
				placeholder_row_event_id: null,
				next_or_previous_cue_id: null,
			};
		},
		makeEventChanges({ keep_event_step, avoid_call_post_event_changes, method_used_to_edit_time } = {}) {
			if (this.is_show_locked == true) {
				return;
			}

			let original_events = JSON.parse(JSON.stringify(this.active_show.events));
			if (this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.shift_events) {
				let entered_time = this.event_edit_attributes.sec_input_value;

				if (entered_time?.replace(/\s/g, '') == '') {
					return;
				}

				const is_negative = entered_time?.charAt(0) == '-';
				if (is_negative) {
					entered_time = entered_time?.substring(1, entered_time.length);
				}

				const entered_time_seconds = convertFormattedStringIntoSeconds(entered_time);

				if (isNaN(entered_time_seconds)) {
					return;
				} else {
					shiftEvents(entered_time_seconds * (is_negative ? -1 : 1), this.active_show.events, this.active_show_selected_events, method_used_to_edit_time);
				}
			}

			let changes_in_progress = false;

			for (const event_id of this.active_show_selected_events) {
				let updated_event = calculateEventTimeWithMethod({
					selected_method: this.event_edit_attributes.selected_event_method,
					event_id,
					events: this.active_show.events,
					min_input_value: this.event_edit_attributes.min_input_value,
					sec_input_value: this.event_edit_attributes.sec_input_value,
					mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value,
					audio_duration: useWaveformStore()?.audio_duration,
					delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
					method_used_to_edit_time,
				});

				if (updated_event != undefined) {
					if (updated_event.event_time.indexOf('Non Number Inputted') != -1 || updated_event.event_time.indexOf('Non Number Inputed') != -1) {
						//Is invalid
						NotificationCenter.add(new NotificationHandler.Notification('Non Number Inputted', 'Some character other than a number was imputed when trying to change the cues event time.', null, NotificationType.WARNING));

						return;
					}
					if (updated_event.event_time.indexOf('Out Of Bounds') != -1 || updated_event.event_time.indexOf('Out Of Audio File Bounds') != -1) {
						//Is invalid
						NotificationCenter.add(new NotificationHandler.Notification('Out Of Bounds', 'The imputed event time is out of the bounds of the audio file.', null, NotificationType.WARNING));
						NotificationCenter.add(
							new NotificationHandler.Notification(
								'Event Time Out of Bounds',
								'The inputted event time is beyond the current show timeline.\n<br><br>If your show has no audio, please increase your estimated show duration within Show Settings.\n<br><br>If your show has audio, you can add silence to the end of your audio file by creating a new (or editing) your combined audio file from the Audio Studio.',
								null,
								NotificationType.WARNING
							)
						);

						if (changes_in_progress == true) {
							// if changes were in progress, then we need to call postEventChanges after the changes are made, to enable undo/redo
							this.postEventChanges(original_events);
						}

						return;
					}

					let original_event = JSON.parse(JSON.stringify(this.active_show.events[updated_event.index]));

					if (this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.step) {
						changeEventValueInArray(updated_event.index, 'isSTEP', true, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'isALTERNATE1', false, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'isALTERNATE2', false, Object.keys(original_event), this.active_show.events);

						// now as per new requirements i.e evs-318 , we need to find the last timed or step event before the current event and use its time as the new event time
						let { previous_event_time, previous_event_time_in_seconds, previous_event_duration_in_seconds } = getPreviousEventDetails(this.active_show.events, updated_event.index);
						if (previous_event_duration_in_seconds > 0) {
							previous_event_time_in_seconds = previous_event_time_in_seconds + previous_event_duration_in_seconds;
							previous_event_time = formatDisplayableTime(previous_event_time_in_seconds);
						}
						changeEventValueInArray(updated_event.index, 'event_time', previous_event_time, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'event_time_in_seconds', previous_event_time_in_seconds, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'timeline_event_time', previous_event_time_in_seconds, Object.keys(original_event), this.active_show.events);

						const next_step_event_row_index = this.step_events.find((step_event) => step_event.row_index_of_step_event > updated_event.index)?.row_index_of_step_event; // to keep the code optimized, it may or may not be undefined here
						const current_event_being_updated = this.active_show.events[updated_event.index];
						const row_index_to_end_adjustments = next_step_event_row_index ?? this.active_show.events.length;
						let first_timed_event_below_step;
						for (let i = updated_event.index + 1; i < row_index_to_end_adjustments; i++) {
							const event = this.active_show.events[i];
							if (event?.isSTEP === true) {
								break;
							}
							if (event?.isALTERNATE1 === true || event?.isALTERNATE2 === true || event?.isCOMMENT === true) {
								continue;
							}
							first_timed_event_below_step = event;
							break;
						}
						if (first_timed_event_below_step != undefined && first_timed_event_below_step.event_time_in_seconds < previous_event_time_in_seconds) {
							const seconds_to_shift_children_by = previous_event_time_in_seconds;
							for (let i = updated_event.index + 1; i < row_index_to_end_adjustments; i++) {
								const event = this.active_show.events[i];
								if (event?.isSTEP === true) {
									break;
								}
								if (event?.isALTERNATE1 === true || event?.isALTERNATE2 === true || event?.isCOMMENT === true) {
									continue;
								}
								if (event.event_time_in_seconds >= previous_event_time_in_seconds) {
									break;
								}
								const shifted_event_time_in_seconds = event.event_time_in_seconds + seconds_to_shift_children_by;
								changeEventValueInArray(i, 'event_time', formatDisplayableTime(shifted_event_time_in_seconds), Object.keys(original_event), this.active_show.events);
								changeEventValueInArray(i, 'event_time_in_seconds', shifted_event_time_in_seconds, Object.keys(original_event), this.active_show.events);
								changeEventValueInArray(i, 'timeline_event_time', shifted_event_time_in_seconds, Object.keys(original_event), this.active_show.events);
							}
						}
						// update the relative times and event times for the step events below the updated event. Since the event time is changed w.r.t to its previous event, we need to update the relative times and event times for the step events below the updated event.
						updateOffsetsAndRelativeTime({ updated_event: current_event_being_updated, event_to_which_step_event_is_snapped_to: undefined, sorting_done_due_to_reduced_event_time: false });

						// to reCalculate relative_event_times starting from current event till end
						calculateEventTimes(this.active_show.events, this.active_show.firmware_version, { start_from_row_index: updated_event.index, end_at_row_index: next_step_event_row_index });
						// to reCreate parent child relation starting from current event till end
						createStepParentChildRelationship({ start_from_row_index: updated_event.index });
						// updated_events_array = calculateEventTimes(updated_events_array, this.active_show.firmware_version);
					} else if (this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.alternate || this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.alternate1 || this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.alternate2) {
						if ((updated_event.event_time == 'isSTEP') == true) {
							changeEventValueInArray(updated_event.index, 'isSTEP', false, Object.keys(original_event), this.active_show.events);
						}

						if (this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.alternate || this.event_edit_attributes.selected_event_method === EVENT_EDIT_METHODS.alternate1) {
							const alternate_event_length = this.active_show.events.filter((event) => event.isALTERNATE1 == true).length;
							if (alternate_event_length >= 18) {
								NotificationCenter.add(new NotificationHandler.Notification('Max Alternate 1 Events', `You have reached the maximum number of alternate 1 events allowed.`, [], NotificationType.ERROR, { has_close_button: true }));
								return;
							}
							changeEventValueInArray(updated_event.index, 'isALTERNATE1', true, Object.keys(original_event), this.active_show.events);
							changeEventValueInArray(updated_event.index, 'isALTERNATE2', false, Object.keys(original_event), this.active_show.events);
						} else {
							const alternate_event_length = this.active_show.events.filter((event) => event.isALTERNATE2 == true).length;
							if (alternate_event_length >= 18) {
								NotificationCenter.add(new NotificationHandler.Notification('Max Alternate 2 Events', `You have reached the maximum number of alternate 2 events allowed.`, [], NotificationType.ERROR, { has_close_button: true }));
								return;
							}
							changeEventValueInArray(updated_event.index, 'isALTERNATE1', false, Object.keys(original_event), this.active_show.events);
							changeEventValueInArray(updated_event.index, 'isALTERNATE2', true, Object.keys(original_event), this.active_show.events);
						}

						if (original_event.isSTEP) {
							const found_event = this.active_show.events.find((event) => event.event_id == event_id);
							updated_event = calculateEventTimeWithMethod({
								selected_method: this.event_edit_attributes.selected_event_method,
								event_id,
								events: this.active_show.events,
								min_input_value: this.event_edit_attributes.min_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.min,
								sec_input_value: this.event_edit_attributes.sec_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.sec,
								mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.milSec,
								audio_duration: useWaveformStore()?.audio_duration,
								delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
							});

							if (keep_event_step !== true) {
								// if step event is dragged in waveform it should remain step.
								changeEventValueInArray(updated_event.index, 'isSTEP', false, Object.keys(original_event), this.active_show.events);
							}
						}

						// if original event was step or child of step, means parent_event from its children should be removed and their relative times should be updated.
						if (original_event.isSTEP || original_event.parent_event != undefined) {
							const next_step_event_row_index = this.step_events.find((step_event) => step_event.row_index_of_step_event > updated_event.index)?.row_index_of_step_event; // to keep the code optimized, it may or may not be undefined here
							// to reCalculate relative_event_times starting from current event till end
							calculateEventTimes(this.active_show.events, this.active_show.firmware_version, { start_from_row_index: updated_event.index, end_at_row_index: next_step_event_row_index });
							// to reCreate parent child relation starting from current event till end
							createStepParentChildRelationship();
						} else {
							// simple update of single timed event, or if any child event exceeds the boundary of its parent.
							updateOffsetsAndRelativeTime({ updated_event });
						}
					} else {
						if (original_event.isSTEP) {
							const found_event = this.active_show.events.find((event) => event.event_id == event_id);
							updated_event = calculateEventTimeWithMethod({
								selected_method: this.event_edit_attributes.selected_event_method,
								event_id,
								events: this.active_show.events,
								min_input_value: this.event_edit_attributes.min_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.min,
								sec_input_value: this.event_edit_attributes.sec_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.sec,
								mil_sec_input_value: this.event_edit_attributes.mil_sec_input_value ?? splitFormatedStringIntoTimeElements(found_event?.event_time)?.milSec,
								audio_duration: useWaveformStore()?.audio_duration,
								delay_sec_input_value: this.event_edit_attributes.delay_sec_input_value,
							});

							if (keep_event_step !== true) {
								// if step event is dragged in waveform it should remain step.
								changeEventValueInArray(updated_event.index, 'isSTEP', false, Object.keys(original_event), this.active_show.events);
							}
						} else if (canEventTimeBeUpdated({ updated_event }) == false) {
							NotificationCenter.add(new NotificationHandler.Notification(t('show_store.step_child_time_update_error_title'), t('show_store.step_child_time_update_error_description'), [], NotificationType.ERROR, { expire_time: 0 }));
							return;
						}

						let new_event_time_in_seconds = convertFormattedStringIntoSeconds(updated_event.event_time + '');
						// If this event is a STEP child being set to its parent's displayed time, snap to the parent's exact stored seconds.
						// Without this, the parent's drifted stored value (e.g. 20.5000000003) can sit just above the child's clean value (20.5),
						// causing the subsequent sort to reorder the child above its parent and the relationship rebuild to reassign it to the previous STEP.
						const parent_step_event_id_for_snap = original_event.parent_event;
						if (parent_step_event_id_for_snap != undefined) {
							const parent_step_event = this.active_show.events.find((event) => event.event_id == parent_step_event_id_for_snap);
							if (parent_step_event != undefined && parent_step_event.event_time_in_seconds != undefined && Number(new_event_time_in_seconds.toFixed(3)) === Number(parent_step_event.event_time_in_seconds.toFixed(3))) {
								new_event_time_in_seconds = parent_step_event.event_time_in_seconds;
							}
						}
						changeEventValueInArray(updated_event.index, 'event_time', updated_event.event_time, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'event_time_in_seconds', new_event_time_in_seconds, Object.keys(original_event), this.active_show.events);
						changeEventValueInArray(updated_event.index, 'timeline_event_time', new_event_time_in_seconds, Object.keys(original_event), this.active_show.events);

						// updated_events_array = calculateEventTimes(updated_events_array, this.active_show.firmware_version);

						if (this.active_show.events[updated_event.index].isALTERNATE1) {
							changeEventValueInArray(updated_event.index, 'isALTERNATE1', false, Object.keys(original_event), this.active_show.events);
						}
						if (this.active_show.events[updated_event.index].isALTERNATE2) {
							changeEventValueInArray(updated_event.index, 'isALTERNATE2', false, Object.keys(original_event), this.active_show.events);
						}

						// if the event_time_in_seconds of current event is reduced then the time of its previous timed_event, sorting should happen first. works only with reduced/decrease time, for increased time it'll be handled in updateOffsetsAndRelativeTime function itself.
						const previous_event = this.active_show.events[updated_event.index - 1];
						const current_event_being_updated = this.active_show.events[updated_event.index];
						let event_to_which_step_event_is_snapped_to;
						let sorting_done_due_to_reduced_event_time = false;
						if (previous_event != undefined && !previous_event.isSTEP && current_event_being_updated.event_time_in_seconds < previous_event.event_time_in_seconds) {
							this.active_show.events = sortEvents(this.active_show.events);
							event_to_which_step_event_is_snapped_to = this.active_show.events[updated_event.index - 1];
							sorting_done_due_to_reduced_event_time = true;
						}

						// if original event was step, means parent_event from its children should be removed and their relative times should be updated.
						if (original_event.isSTEP) {
							let start_from_row_index = updated_event.index;
							const previous_step_event_row_index = this.step_events.find((step_event) => step_event.row_index_of_step_event < updated_event.index)?.row_index_of_step_event;
							if (previous_step_event_row_index != undefined) {
								// if the updated event is not a step event, and there's a step event above this event, then we need to update the relative times of the events above this event.
								start_from_row_index = previous_step_event_row_index;
							}
							const next_step_event_row_index = this.step_events.find((step_event) => step_event.row_index_of_step_event > updated_event.index)?.row_index_of_step_event; // to keep the code optimized, it may or may not be undefined here

							// simple update of single timed event, or if any child event exceeds the boundary of its parent.
							updateOffsetsAndRelativeTime({ updated_event: current_event_being_updated, event_to_which_step_event_is_snapped_to, sorting_done_due_to_reduced_event_time });

							// to reCalculate relative_event_times starting from current event till end/next step event
							calculateEventTimes(this.active_show.events, this.active_show.firmware_version, { start_from_row_index, end_at_row_index: next_step_event_row_index });
							// to reCreate parent child relation starting from current event till end
							createStepParentChildRelationship({ start_from_row_index });
						} else {
							// simple update of single timed event, or if any child event exceeds the boundary of its parent.
							updateOffsetsAndRelativeTime({ updated_event: current_event_being_updated, event_to_which_step_event_is_snapped_to, sorting_done_due_to_reduced_event_time });
						}
					}

					// After changing time of an event check the event for any breaking rules/errors/warnings
					this.validateCellOnValueChange(this.active_show.events.find((e) => e.event_id == event_id));
				}

				changes_in_progress = true;
			}

			// If avoid_call_post_event_changes is set to true do not call postEventChanges as the event changes are being made in a loop and calling it on each iteration will take toll on performance.
			// EVS-1147: also do not register undo here — the caller (e.g. waveform drag end) owns one postEventChanges(original_events) for the whole gesture.
			if (avoid_call_post_event_changes != undefined && avoid_call_post_event_changes === true) {
				return;
			}

			this.postEventChanges(original_events);
		},
		makeEventChangesAsPerRelativeTime() {
			for (const event_id of this.active_show_selected_events) {
				const event = this.active_show.events.find((event) => event.event_id == event_id);

				// Compute the shift in precise seconds rather than doing digit-by-digit math on the displayed time strings.
				// The displayed event_time can be a truncated render of a drifted value (e.g. 291.71999997 shows as "04:51.71"),
				// which previously made the result land one centisecond below the intended time.
				const new_relative_time_in_seconds = convertFormattedStringIntoSeconds(`${this.event_edit_attributes?.relative_min_input_value}:${this.event_edit_attributes?.relative_sec_input_value}.${this.event_edit_attributes?.relative_mil_sec_input_value}`);
				const current_relative_time_in_seconds = event.relative_event_time_in_seconds ?? convertFormattedStringIntoSeconds(event.relative_event_time + '');
				const delta_in_seconds = new_relative_time_in_seconds - current_relative_time_in_seconds;

				// Round to milliseconds to strip accumulated floating-point drift before formatting.
				const new_event_time_in_seconds = Number((event.event_time_in_seconds + delta_in_seconds).toFixed(3));
				const new_event_time = formatDisplayableTime(new_event_time_in_seconds + '');
				const new_event_time_parts = splitFormatedStringIntoTimeElements(new_event_time);

				this.setNewTime(new_event_time_parts.min, event.event_id, splitFormatedStringIntoTimeElements(event.event_time), 'minute');
				this.setNewTime(new_event_time_parts.sec, event.event_id, splitFormatedStringIntoTimeElements(event.event_time), 'second');
				this.setNewTime(new_event_time_parts.milSec, event.event_id, splitFormatedStringIntoTimeElements(event.event_time), 'milli_sec');

				this.makeEventChanges({ method_used_to_edit_time: METHOD_TO_EDIT_TIME.EDIT_RELATIVE_EVENT_TIME });
			}
		},
		selectRowsToggle() {
			// Use the same source of truth the events table renders from, so the count can never
			// drift from what is actually on screen.
			const visible_event_count = this.filtered_events_based_on_show_type.length;

			if (visible_event_count == this.active_show_selected_events.length) {
				//All rows already selected, unselect all of them;
				this.unselectRows();
				return;
			}

			this.selectRowsInRange(0, this.active_show?.events.length - 1);
		},
		selectRowsInRange(starting_index = undefined, ending_index = undefined) {
			if (starting_index == undefined || starting_index == null) {
				console.warn('debug [Cue Table][Select Rows In Range] No Starting Index passed!');
				return;
			}
			if (ending_index == undefined || starting_index == null) {
				console.warn('debug [Cue Table][Select Rows In Range] No Ending Index passed!');
				return;
			}

			// check if the select all events needs to be triggered
			if (starting_index == 0 && ending_index == this.active_show.events.length - 1 && this.active_show.show_attributes.show_type == SHOW_TYPES.PYRO_DMX.key) {
				this.selectAnEvent({ select_all_events: true });
				return;
			}

			// Ensure DMX events are not copied from a Pyro show and vice versa. This mirrors the events
			// table exactly instead of re-implementing the visibility rule here.
			const visible_event_ids = new Set(this.filtered_events_based_on_show_type.map((visible_event) => visible_event.event_id));

			for (var index = starting_index; index < this.active_show.events.length; index++) {
				if (index > ending_index) {
					break;
				} //Include the ending index, then stop looping
				const event = this.active_show.events[index];

				if (visible_event_ids.has(event.event_id) == false) {
					continue;
				}

				const event_id = event.event_id;
				const firework_name = event.firework_name;
				if (this.active_show_selected_events.includes(event_id)) {
					continue;
				}
				this.selectAnEvent({
					event_id,
					firework_name,
					select_multiple: true,
				});
			}
		},
		unselectRows() {
			clearSelectedRowsBackgroundColor();

			this.active_show_selected_events = [];
			this.active_show_selected_fireworks = [];
			this.scroll_to_event_id = null;
		},
		// updateNextStepEvent() {
		// 	const events_from_index = this.active_show.events.slice(this.next_step_event_index);
		// 	this.previous_step_event_index = this.next_step_event_index;

		// 	let second_occurence_index = events_from_index.findIndex((event) => event.isSTEP === true);

		// 	if (second_occurence_index !== -1) {
		// 		// Calculate the original index of the second event
		// 		const original_index = this.next_step_event_index + second_occurence_index + 1;

		// 		if (this.active_show.events[original_index]?.timeline_event_time.toFixed(2) == this.active_show.events[this.previous_step_event_index]?.timeline_event_time.toFixed(2) && this.active_show.events[this.next_step_event_index].STEPhasNoChildEvents == true) {
		// 			this.consective_pause_play = true;
		// 		} else {
		// 			this.consective_pause_play = false;
		// 		}
		// 		this.next_step_event_index = original_index;
		// 	} else {
		// 		this.next_step_event_index = undefined;
		// 		this.previous_step_event_index = undefined;
		// 		this.setPausedAtStep(false);
		// 	}
		// },
		// setPausedAtStep(value) {
		// 	this.paused_at_step = value;
		// },
		setInsertDraggedEventAtIndex(index) {
			this.insert_dragged_event_at_index = index;
		},
		async saveShow({ is_auto_save = false, force_save = false, non_active_show = undefined, save_triggered_from = SAVE_TRIGGERED_FROM.SIDEBAR_BUTTON } = {}) {
			// now handle the updation of non_active_show as well, this will be triggered by offline sync
			if (non_active_show != undefined) {
				// to handle the case where user converts a step event to non step event and vice versa. so instead of validating this on every add, update of events, we will do this here.
				const has_wrong_modified_by_elevate_flag = !!non_active_show.events?.find((event) => (event.isSTEP == true && event.converted_to_STEP_v2 != true) || (event.isSTEP != true && event.converted_to_STEP_v2 == true));
				if (has_wrong_modified_by_elevate_flag == true) {
					// this function also modifies converted_to_STEP_v2 flag of step events to true
					non_active_show.events = synchronizeEventTimesAndUpdateFlags(non_active_show.events);
				}

				let show_data = {};

				if (force_save == true) {
					show_data.show_ID = this.force_update_show_details.show_ID;
					show_data.firmware_version = this.force_update_show_details.firmware_version;
					show_data.uses_audio = this.force_update_show_details.script_atributes.use_audio_box;
					// show_data.audio_file_id = this.force_update_show_details.audio_file_id; // EVS-1058: audio_file_id is set exclusively via the dedicated audio endpoint; including it here allows stale sessions to overwrite the server's correct value
					show_data.audio_box_filename = this.force_update_show_details.audio_box_filename;
					show_data.script_name = this.force_update_show_details.script_name;
					show_data.custom_name = this.force_update_show_details.custom_name;
					show_data.custom_columns = this.force_update_show_details.custom_columns;
					show_data.script_atributes = this.force_update_show_details.script_atributes;
					show_data.cues = this.force_update_show_details.cues;
					show_data.estimated_show_duration = this.force_update_show_details.estimated_show_duration;
					show_data.disable_firing_groups = this.force_update_show_details.disable_firing_groups || []; // recently added
					show_data.last_known_updated = this.force_update_show_details.last_edited ?? this.force_update_show_details.last_known_updated;
					show_data.force_overwrite = true;
					show_data.save_triggered_from = save_triggered_from;
					show_data.defined_positions = this.force_update_show_details.defined_positions || [];
				} else {
					const detransformed_data = deTransformScriptArray(non_active_show);
					console.log('detransformed_data non_active_show', detransformed_data);

					show_data.show_ID = detransformed_data[0].show_ID;
					show_data.firmware_version = detransformed_data[0].firmware_version;
					show_data.uses_audio = detransformed_data[0].script_atributes.use_audio_box;
					// show_data.audio_file_id = detransformed_data[0].audio_file_id; // EVS-1058: audio_file_id is set exclusively via the dedicated audio endpoint; including it here allows stale sessions to overwrite the server's correct value
					show_data.audio_box_filename = detransformed_data[0].audio_box_filename;
					show_data.script_name = detransformed_data[0].script_name;
					show_data.custom_name = detransformed_data[0].custom_name;
					show_data.custom_columns = detransformed_data[0].custom_columns;
					show_data.script_atributes = detransformed_data[0].script_atributes;
					show_data.cues = detransformed_data[0].cues;
					show_data.estimated_show_duration = detransformed_data[0].estimated_show_duration;
					show_data.disable_firing_groups = detransformed_data[0].disable_firing_groups || []; // recently added
					show_data.last_known_updated = detransformed_data[0].last_edited;
					show_data.save_triggered_from = save_triggered_from;
					show_data.defined_positions = detransformed_data[0].defined_positions || [];
				}

				try {
					const response = await saveShow(show_data);
					if (response.error != undefined) {
						throw new Error(JSON.stringify(response.error));
					} else {
						// make sure save is not triggered from offline sync or offline sync force save, because their cleanup is already ahandled in the offlinemode perform sync function itself.
						if (save_triggered_from != SAVE_TRIGGERED_FROM.OFFLINE_SYNC && save_triggered_from != SAVE_TRIGGERED_FROM.OFFLINE_SYNC_FORCE_SAVE) {
							OfflineModeEditsPendingSyncManager.handleCleaningUpOfflineEditsPendingSync(non_active_show);
						}
					}
				} catch (error) {
					console.log('error in saveShow non_active_show', error);
					throw new Error(JSON.stringify(error));
				}
			} else if (this.active_show != undefined) {
				//&& this.a_change_has_been_made == true
				this.show_saved = false;
				this.show_saving_status.error_saving = undefined;
				this.show_saving_status.is_saving = true;

				if (this.show_saving_status.latest_saved_locally_at != undefined) {
					this.show_saving_status.previous_saved_locally_at = this.show_saving_status.latest_saved_locally_at;
				}

				if (this.show_saving_status.latest_saved_to_cloud_at != undefined) {
					this.show_saving_status.previous_saved_to_cloud_at = this.show_saving_status.latest_saved_to_cloud_at;
				}
				this.show_saving_status.latest_saved_locally_at = undefined;
				this.show_saving_status.latest_saved_to_cloud_at = undefined;

				// to handle the case where user converts a step event to non step event and vice versa. so instead of validating this on every add, update of events, we will do this here.
				const has_wrong_modified_by_elevate_flag = !!this.active_show.events?.find((event) => (event.isSTEP == true && event.converted_to_STEP_v2 != true) || (event.isSTEP != true && event.converted_to_STEP_v2 == true));
				if (has_wrong_modified_by_elevate_flag == true) {
					// this function also modifies converted_to_STEP_v2 flag of step events to true
					this.active_show.events = synchronizeEventTimesAndUpdateFlags(this.active_show.events);
				}
				const events_summary_for_save_log = (this.active_show?.events || []).slice(0, 500).map((ev) => ({
					event_id: ev?.event_id,
					channel: ev?.channel,
					cue: ev?.cue,
					cue_auto_incremented: ev?.cue_auto_incremented,
					isDMX: ev?.isDMX,
				}));
				console.log('[EVS-981_CC_DEBUG] saveShow ENTER (active_show path)', {
					show_id: this.active_show?.show_ID,
					save_triggered_from,
					is_auto_save,
					force_save,
					event_count: this.active_show?.events?.length,
					events_summary_first_500: events_summary_for_save_log,
				});

				let show_data = {};
				if (force_save == true) {
					show_data.show_ID = this.force_update_show_details.show_ID;
					show_data.firmware_version = this.force_update_show_details.firmware_version;
					show_data.uses_audio = this.force_update_show_details.script_atributes.use_audio_box;
					// show_data.audio_file_id = this.force_update_show_details.audio_file_id; // EVS-1058: audio_file_id is set exclusively via the dedicated audio endpoint; including it here allows stale sessions to overwrite the server's correct value
					show_data.audio_box_filename = this.force_update_show_details.audio_box_filename;
					show_data.script_name = this.force_update_show_details.script_name;
					show_data.custom_name = this.force_update_show_details.custom_name;
					show_data.custom_columns = this.force_update_show_details.custom_columns;
					show_data.script_atributes = this.force_update_show_details.script_atributes;
					show_data.cues = this.force_update_show_details.cues;
					show_data.estimated_show_duration = this.force_update_show_details.estimated_show_duration;
					show_data.disable_firing_groups = this.force_update_show_details.disable_firing_groups || []; // recently added
					show_data.last_known_updated = this.force_update_show_details.last_edited ?? this.force_update_show_details.last_known_updated;
					show_data.force_overwrite = true;
					show_data.save_triggered_from = save_triggered_from;
					show_data.defined_positions = this.force_update_show_details.defined_positions || [];
				} else {
					const detransformed_data = deTransformScriptArray(this.active_show);
					console.log('detransformed_data', detransformed_data);

					show_data.show_ID = detransformed_data[0].show_ID;
					show_data.firmware_version = detransformed_data[0].firmware_version;
					show_data.uses_audio = detransformed_data[0].script_atributes.use_audio_box;
					// show_data.audio_file_id = detransformed_data[0].audio_file_id; // EVS-1058: audio_file_id is set exclusively via the dedicated audio endpoint; including it here allows stale sessions to overwrite the server's correct value
					show_data.audio_box_filename = detransformed_data[0].audio_box_filename;
					show_data.script_name = detransformed_data[0].script_name;
					show_data.custom_name = detransformed_data[0].custom_name;
					show_data.custom_columns = detransformed_data[0].custom_columns;
					show_data.script_atributes = detransformed_data[0].script_atributes;
					show_data.cues = detransformed_data[0].cues;
					show_data.estimated_show_duration = detransformed_data[0].estimated_show_duration;
					show_data.disable_firing_groups = detransformed_data[0].disable_firing_groups || []; // recently added
					show_data.last_known_updated = detransformed_data[0].last_edited;
					show_data.save_triggered_from = save_triggered_from;
					show_data.defined_positions = detransformed_data[0].defined_positions || [];
				}

				// 0. Generate hash using only content fields — mirrors BE hashShowData, prevents metadata changes (e.g. last_known_updated) from triggering spurious saves.
				// Computed now, immediately after show_data is built and before any of the awaited I/O below, so it reflects the exact snapshot being saved and can't absorb edits made while the save is in flight (script_atributes is a live reference to active_show, not a clone).
				let should_save_to_API = true;
				const show_hash = await sha256HashOfObject(buildShowHashPayload(show_data));
				// Snapshot the edit counter too, so we know at the end whether the user edited again while this save was in flight before clearing "unsaved changes".
				const edits_count_before_save = this.edits_since_last_save;
				if (is_auto_save == true) {
					// 1. Compare with last hash
					should_save_to_API = this.last_save_show_hash == undefined || this.last_save_show_hash !== show_hash;
				}
				console.log('log should_save_to_API : ', should_save_to_API);
				// 3. Always save to indexDB/revision
				try {
					await OfflineModeShowManager.updateShow(show_data);
					console.log('Stored show locally');
				} catch (error) {
					console.error('Error saving local copy of show', error);
					window.plausible('Error saving local show', { props: { error: JSON.stringify(error), version: VERSION_NUMBER, build_number: BUILD_NUMBER } });
					NotificationCenter.add(
						new NotificationHandler.Notification('Possible Error Saving Local Copy', `We detected a possible issue with saving a local copy of this show. This should have no effect on saving your show, however, as a precaution a copy of your show has been downloaded. Please contact us at help@cobrafiringsystems.com.`, [], NotificationType.WARNING, { expire_time: 0 })
					);
					// RevisionHistoryManager._downloadJSONFile(show_data, `${show_data.show_ID}_local_copy_${new Date().getTime()}`);
					window._downloadJSONFile(show_data, `${show_data.show_ID}_local_copy_${new Date().getTime()}`);
				}

				// Keeping this on hold.
				// if (this.info_about_loaded_song?.file_ID != undefined) {
				// 	show_data.audio_file_id = this.info_about_loaded_song?.file_ID;
				// }

				try {
					await saveRevision(show_data);
				} catch (error) {
					console.error('Error saving revision', error);
					window.plausible('Error saving revision', { props: { error: JSON.stringify(error), version: VERSION_NUMBER, build_number: BUILD_NUMBER } });
					NotificationCenter.add(new NotificationHandler.Notification('Possible Error Saving Local Revision', `We detected a possible issue with saving a local revision history. This has no effect on saving your show! Please contact us at help@cobrafiringsystems.com.`, [], NotificationType.WARNING, { expire_time: 0 }));
				}

				if (should_save_to_API == true) {
					try {
						const response = await saveShow(show_data);
						if (response.error != undefined) {
							if (response.error?.title == 'NO_NETWORK_CONNECTION') {
								OfflineModeEditsPendingSyncManager.saveOfflineEdit({ entity: 'show', entity_id: show_data.show_ID, action: 'update', entity_data: show_data });
								this.show_saving_status.latest_saved_locally_at = new Date().getTime();
							} else {
								throw new Error(JSON.stringify(response.error));
							}

							// NOTE: uncomment for debugging
							// try {
							// 	submitShowRevisionLog({ show_id: show_data.show_ID, was_successful: 0, was_offline_sync: 0, events: show_data.cues, error: JSON.stringify(response.error) });
							// } catch (error) {
							// 	console.log('Error submitting show revision log');
							// }
						} else {
							this.show_saving_status.latest_saved_to_cloud_at = new Date().getTime();

							if (response.show?.last_edited != undefined) {
								// ← always true for successful save, but adding an extra check just to be safe before updating the last_edited in active_show
								this.active_show.last_edited = response.show?.last_edited;
							} else if (response.last_edited != undefined) {
								this.active_show.last_edited = response.last_edited;
							}

							if (response.status == 'CONFLICT_DETECTED') {
								window.plausible('CONFLICT_DETECTED_SAVING_SHOW', { props: { response: JSON.stringify(response), version: VERSION_NUMBER, build_number: BUILD_NUMBER } });
								await this.setForceUpdateShowDetails({
									...show_data,
									last_edited_on_server: response.last_edited,
									cloud_events_count: response.cloud_events_count,
								});
							}

							// make sure save is not triggered from offline sync or offline sync force save, because their cleanup is already ahandled in the offlinemode perform sync function itself.
							if (save_triggered_from != SAVE_TRIGGERED_FROM.OFFLINE_SYNC && save_triggered_from != SAVE_TRIGGERED_FROM.OFFLINE_SYNC_FORCE_SAVE) {
								OfflineModeEditsPendingSyncManager.handleCleaningUpOfflineEditsPendingSync(this.active_show);
							}

							// Only update the hash baseline when the save actually committed to the BE.
							// On CONFLICT_DETECTED the BE does not write, so keeping the old hash ensures
							// auto-saves will retry once the conflict is resolved.
							if (response.status != 'CONFLICT_DETECTED') {
								this.last_save_show_hash = show_hash;
							}

							// NOTE: uncomment for debugging
							// try {
							// 	submitShowRevisionLog({ show_id: show_data.show_ID, was_successful: 1, was_offline_sync: 0, events: show_data.cues, error: null });
							// } catch (error) {
							// 	console.log('Error submitting show revision log');
							// }
						}

						// to check when to highlight save show button
						this.show_saved = true;
						this.show_saving_status.is_saving = false;
						// Only clear "unsaved changes" if nothing was edited while this save was in flight - otherwise the button would wrongly show "saved" for an edit that hasn't reached the server yet.
						// Also skip on CONFLICT_DETECTED - the BE did not persist this save, so the edit is still genuinely unsaved while conflict resolution is pending.
						if (response.status != 'CONFLICT_DETECTED' && this.edits_since_last_save === edits_count_before_save) {
							this.setChangeMadeToFalse();
						}

						// to set the latest updated show in store state i.e acitve_show.
						// this.reFetchActiveShowDetails(); // no need to update the show object

						useInventoryStore()?.setTriggerSyncInventoryChanges(true);

						const setting_store = useSettingStore();
						setting_store.elevate_studio_user_actions_broadcast_channel.postMessage({ title: 'CSC_user_saved_edits_to_show', show_id: this.active_show.show_ID });

						setting_store.is_fireworks_section_refresh_button_highlighted = true;
					} catch (error) {
						this.show_saving_status.is_saving = false;
						this.show_saved = false;
						this.show_saving_status.error_saving = error;

						// NOTE: uncomment for debugging
						// const error_object = {
						// 	message: error.message,
						// 	stack: error.stack,
						// 	name: error.name,
						// };
						// submitShowRevisionLog({ show_id: show_data.show_ID, was_successful: 0, was_offline_sync: 0, events: show_data.cues, error: error_object });
						throw new Error(JSON.stringify(error));
					}
				} else {
					this.show_saved = true;
					this.show_saving_status.is_saving = false;
					this.show_saving_status.latest_saved_to_cloud_at = this.show_saving_status.previous_saved_to_cloud_at;
					this.show_saving_status.latest_saved_locally_at = this.show_saving_status.previous_saved_locally_at;
					// Content already matches the last save (that's why should_save_to_API was false) - clear "unsaved changes" unless the user edited again while this check was running.
					if (this.edits_since_last_save === edits_count_before_save) {
						this.setChangeMadeToFalse();
					}
				}
			}

			// in the end return true when no errors were found, to send was_sync_successful = true to OfflineModeEditsPendingSyncManager
			return true;
		},
		highlightColumnOfActiveRow(event_id, found_element, { is_custom_column = false, custom_column_name = undefined } = {}) {
			if (event_id != undefined && found_element != undefined) {
				this.highlight_column = {
					event_id,
					column_indexes: found_element.column_indexes,
					word: found_element.word,
					is_custom_column,
					custom_column_name,
				};
			}
		},
		clearHighlightedColumnOfActiveRow() {
			this.highlight_column = {
				event_id: null,
				column_indexes: [],
				is_custom_column: false,
			};
		},
		setChangeMadeToFalse() {
			this.a_change_has_been_made = false;
		},
		setChangeMadeToTrue() {
			this.a_change_has_been_made = true;
			this.edits_since_last_save++;
			// clearInterval(this.save_show_interval);

			if (this.save_show_interval == undefined) {
				this.save_show_interval = setInterval(async () => {
					// only allow auto save if the conflict modal is not open
					if (this.force_update_show_details != undefined) {
						return;
					}
					try {
						const auth_store = useAuthStore();
						// if Admin has loaded another users show
						if ((this.active_show && this.active_show.users_UUID != auth_store.user_info?.UUID && auth_store.user_info?.has_admin_privileges == true) || this.enable_auto_save == false) {
							return;
						}
						this.saveShow({ is_auto_save: true, save_triggered_from: SAVE_TRIGGERED_FROM.AUTO_SAVE });
					} catch (error) {
						console.log('🚀 ~ this.save_show_interval=setTimeout ~ error:', error);
					}
				}, 60000);
			}

			const setting_store = useSettingStore();
			setting_store.elevate_studio_user_actions_broadcast_channel.postMessage({ title: 'CSC_user_saved_edits_to_show', show_id: this.active_show.show_ID });
		},
		setTotalNumberOfColumns(total_columns) {
			this.active_show_total_columns = total_columns;
		},

		toggleWaveformReset() {
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
		},
		onConfirmDeleteEvents() {
			const original_events = JSON.parse(JSON.stringify(this.active_show.events));
			this.newly_updated_selected_events = this.active_show?.events?.filter((event) => this.active_show_selected_events?.includes(event.event_id));

			if (this.active_show_selected_events.length == this.active_show.events.length) {
				// no need to create parent child relations in this case
				this.active_show.events = [];
			} else {
				const index_of_top_most_deleted_row = this.active_show.events.findIndex((event) => event.event_id == this.active_show_selected_events[0]);

				for (let i = 0; i < this.active_show_selected_events.length; i++) {
					const selected_event_id = this.active_show_selected_events[i];
					const event_index = this.active_show.events.findIndex((event) => event.event_id == selected_event_id);
					if (event_index > -1) {
						const deleted_event = this.active_show.events.find((event) => event.event_id == selected_event_id);
						this.active_show.events.splice(event_index, 1);
						// if deleted event is step, then reCreate parent child relations
						if (deleted_event.isSTEP == true) {
							createStepParentChildRelationship();
						}
						// check if the next event is step, if yes then update the relative times
						if (this.active_show.events[event_index]?.isSTEP == true) {
							this.setStepEventsList(); // the step_events state needs to be updated, as the step_event list is used in updateOffsetsAndRelativeTime

							// start from previous event of deleted event
							const updated_event = this.active_show.events[index_of_top_most_deleted_row - 1 > 0 ? index_of_top_most_deleted_row - 1 : 0];
							updateOffsetsAndRelativeTime({ updated_event });
						}
					}
				}
			}

			// this handles the reCalculation of relative_time & relative_time_in_seconds as calculateEventTimes gets called in here.
			this.active_show.events = validateEvents(this.active_show);
			// this.active_show.events = correctTimelineEventTime(this.active_show.events);
			this.active_show.show_type = updateShowType(this.active_show.events);
			this.setStepEventsList();
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.deleteEvent;

			this.active_show_selected_events = [];
			this.active_show_selected_fireworks = [];
			this.setChangeMadeToTrue();
			useInventoryStore()?.setTriggerFindInventoryChanges();

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setStepEventsList(); // to get the updated step_events list during delete & undo delete events
				this.setChangeMadeToTrue();
			});
		},
		scrollToRow(event_id) {
			this.scroll_to_event_id = event_id;
		},
		removeDisableGroup(event_id) {
			const event = this.active_show.events.find((show_event) => show_event.event_id == event_id);
			if (event == undefined) {
				return;
			}
			if (event.disable_firing_group == null || event.disable_firing_group == undefined) {
				return;
			}

			const original_events = JSON.parse(JSON.stringify(this.active_show.events));
			event.disable_firing_group = null;
			this.setChangeMadeToTrue();

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setChangeMadeToTrue();
			});
		},
		removeDefinedPosition(event_id) {
			const event = this.active_show.events.find((show_event) => show_event.event_id == event_id);
			if (event == undefined) {
				return;
			}
			if (event.defined_position == null || event.defined_position == undefined) {
				return;
			}

			const original_events = JSON.parse(JSON.stringify(this.active_show.events));
			event.defined_position = null;
			this.setChangeMadeToTrue();

			const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
			const undo_manager = useUndoManagerStore();
			undo_manager.addAction(post_update_events, original_events, (previous_events) => {
				this.active_show.events = previous_events;
				this.setChangeMadeToTrue();
			});
		},
		updateDefinedPosition(updated_position, previous_label) {
			if (this.active_show == undefined || !Array.isArray(this.active_show.defined_positions)) return;
			const target = this.active_show.defined_positions.find((position) => position.id == updated_position.id);
			if (target == undefined) return;

			target.label = updated_position.label;
			target.color = updated_position.color;
			target.updated_on = new Date().getTime();

			// Match by id OR by previous_label: backend historically overwrote FE ids on some events,
			// so event-embedded ids can diverge from the defined_positions entry.
			for (const event of this.active_show.events ?? []) {
				if (event?.defined_position == undefined) continue;
				const matches_by_id = event.defined_position.id != undefined && event.defined_position.id == target.id;
				const matches_by_label = previous_label != undefined && event.defined_position.label == previous_label;
				if (matches_by_id || matches_by_label) {
					event.defined_position = { ...event.defined_position, id: target.id, label: target.label, color: target.color };
				}
			}
			this.setChangeMadeToTrue();
		},
		updateDisableFiringGroup(updated_group, previous_label) {
			if (this.active_show == undefined || !Array.isArray(this.active_show.disable_firing_groups)) return;
			const target = this.active_show.disable_firing_groups.find((group) => group.id == updated_group.id);
			if (target == undefined) return;

			target.label = updated_group.label;
			target.color = updated_group.color;
			target.updated_on = new Date().getTime();

			for (const event of this.active_show.events ?? []) {
				if (event?.disable_firing_group == undefined) continue;
				const matches_by_id = event.disable_firing_group.id != undefined && event.disable_firing_group.id == target.id;
				const matches_by_label = previous_label != undefined && event.disable_firing_group.label == previous_label;
				if (matches_by_id || matches_by_label) {
					event.disable_firing_group = { ...event.disable_firing_group, id: target.id, label: target.label, color: target.color };
				}
			}
			this.setChangeMadeToTrue();
		},
		getTotalFireworksInShow() {
			let result = [];
			if (this.active_show.events) {
				result = this.active_show.events.map((event) => {
					if (event?.my_firework_id) {
						return event.my_firework_id;
					}
				});
			}
			return result;
		},
		unlinkFirework({ event_to_update = undefined, new_firework_name = undefined, dom_update_cell_event = undefined, clear_only_description = false } = {}) {
			/**
			 * Unlinks the inventory item from the selected events in the active show.
			 * This function iterates over each selected event in the active show and
			 * After unlinking the inventory item, the function calls the setChangeMadeToTrue
			 *
			 * Context-menu unlink (no event_to_update) registers its own undo entry.
			 * Description-cell unlink is already wrapped by updateDescription's addAction — do not double-register here.
			 */

			if (this.active_show_selected_events?.length <= 0) {
				return;
			}
			const unlinkFunction = () => {
				const is_context_menu_unlink = event_to_update == undefined && new_firework_name == undefined;
				let original_events = undefined;
				if (is_context_menu_unlink == true) {
					original_events = JSON.parse(JSON.stringify(this.active_show.events));
				}

				// Precompute a map from event_id to event object
				const event_map = new Map(this.active_show.events.map((event) => [event.event_id, event]));
				let did_unlink_any = false;

				for (const event_id of this.active_show_selected_events) {
					const event = event_map.get(event_id);
					if (event == undefined || event.my_firework_id == undefined) {
						continue;
					}
					did_unlink_any = true;
					if (clear_only_description == true) {
						event.firework_name = undefined;
					} else {
						event.firework_name = undefined;
						event.duration = undefined;
						event.duration_in_seconds = undefined;
						event.lift_time = undefined;
						event.lift_time_in_seconds = undefined;
					}

					event.my_firework_id = undefined;
				}

				if (is_context_menu_unlink == true) {
					this.unselectRows();
					this.setChangeMadeToTrue();

					if (did_unlink_any == true && original_events != undefined) {
						const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
						const undo_manager = useUndoManagerStore();
						undo_manager.addAction(post_update_events, original_events, (previous_events) => {
							this.active_show.events = previous_events;
							this.setChangeMadeToTrue();
						});
					}
				} else {
					this.updateEventDescription(event_to_update, new_firework_name);
				}
			};

			if (useSettingStore()?.settings.should_ask_before_unlinking_my_fireworks_from_events === true) {
				let cancel_button = new Modal.Button(
					'No',
					() => {
						if (event_to_update != undefined && new_firework_name != undefined && dom_update_cell_event != undefined) {
							dom_update_cell_event.target.textContent = event_to_update.firework_name;
						}
					},
					{ inverted: true, border_inverted: true, bigger: true }
				);
				let confirm_button = new Modal.Button(
					'Yes, Unlink',
					async () => {
						unlinkFunction();
					},
					{ destructive: true, bigger: true, dismiss_modal_after_click: true, primary_action_button: true }
				);

				Modal.createInteractiveModal(`Unlink Firework?`, `Are you sure you want to unlink the firework from the selected events?`, null, [cancel_button, confirm_button], { destroy_on_dismissal: true, dismissible: false });
			} else {
				unlinkFunction();
			}
		},
		syncMyFireworkValuesForEvents(event_ids = undefined) {
			if (this.is_show_locked == true) return;
			if (this.active_show == undefined) return;

			const target_ids = event_ids ?? this.active_show_selected_events ?? [];
			if (target_ids.length === 0) return;

			const firework_store = useFireworkStore();
			const event_map = new Map(this.active_show.events.map((event) => [event.event_id, event]));
			let updated_count = 0;

			for (const event_id of target_ids) {
				const event = event_map.get(event_id);
				if (event == undefined || event.my_firework_id == undefined) continue;
				if (!isMyFireworkOutOfSync(event, firework_store)) continue;

				const firework = firework_store.getFireworkInfo(event.my_firework_id);
				if (firework == undefined) continue;

				syncEventToMyFirework(event, firework);

				const event_index = this.active_show.events.findIndex((e) => e.event_id === event_id);
				if (event_index > -1) {
					event.negative_calculated_event_time = calculateEventTimeForEvent(event, this.active_show.firmware_version).negative_calculated_time;
					validateEvent(this.active_show, event, event_index);
				}
				updated_count++;
			}

			if (updated_count > 0) {
				this.setChangeMadeToTrue();
			}
		},
		updateActiveShowName(new_show_name) {
			if (this.active_show != undefined) {
				this.active_show.custom_name = new_show_name;
			}
		},
		setStepEventsList() {
			this.step_events = [];
			const events = this.active_show.events;
			let index = 0;
			for (let event of events) {
				if (event.isSTEP == true) {
					const row_index_of_step_event = index;
					this.step_events.push({
						event_info: event,
						row_index_of_step_event,
					});
				}
				index++;
			}
		},
		postEventChanges(original_events = undefined) {
			this.active_show.events = sortEvents(this.active_show.events);
			this.setStepEventsList();
			this.active_show.show_type = updateShowType(this.active_show.events);

			this.closeEventEditModal();
			this.setChangeMadeToTrue();

			if (original_events != undefined) {
				const post_update_events = JSON.parse(JSON.stringify(this.active_show.events));
				const undo_manager = useUndoManagerStore();
				undo_manager.addAction(post_update_events, original_events, (previous_events) => {
					this.active_show.events = previous_events;
					this.setChangeMadeToTrue();
				});
			}

			this.newly_updated_selected_events = this.active_show.events.filter((event) => this.active_show_selected_events.includes(event.event_id) === true);
			this.toggle_waveform_reset = !this.toggle_waveform_reset;
			this.action_being_performed = SHOW_METHODS.makeEventChanges;
		},
		setShouldHideLiftTimeColumn() {
			const should_hide_lift_time_columns = useSettingStore()?.settings?.should_hide_lift_time_column;
			const events = this.active_show?.events;
			const setting_store = useSettingStore();
			if (should_hide_lift_time_columns === true) {
				if (events.some((event) => event?.lift_time_in_seconds != undefined && event?.lift_time_in_seconds > 0)) {
					// setting_store.updateSetting('should_hide_lift_time_column', false);

					return;
				} else {
					setting_store.updateSetting('should_hide_lift_time_column', true);
					return;
				}
			}
		},
		setShouldHidePrefireColumn() {
			const setting_store = useSettingStore();
			if (setting_store?.settings?.should_hide_prefire_column === true) {
				if (this.active_show?.events?.some((event) => event?.igniter_pre_fire_in_seconds != undefined && event?.igniter_pre_fire_in_seconds > 0)) {
					setting_store.updateSetting('should_hide_prefire_column', false);
					return;
				} else {
					setting_store.updateSetting('should_hide_prefire_column', true);
					return;
				}
			} else {
				setting_store.updateSetting('should_hide_prefire_column', false);

				return;
			}
		},
		triggerFindAndReplaceSearch() {
			this.trigger_find_and_replace_search = !this.trigger_find_and_replace_search;
		},
		stopAutoSaveShow() {
			if (this.save_show_interval) {
				clearInterval(this.save_show_interval);
				this.save_show_interval = undefined;
				console.log('Auto-save has been stopped');
			}

			this.enable_auto_save = false;
		},
		enableAutoSaveShow() {
			this.enable_auto_save = true;
		},
		setForceUpdateShowDetails(data) {
			return new Promise((resolve) => {
				// 1. Store the data to trigger the modal
				this.force_update_show_details = data;
				// 2. Save the `resolve` function to call later
				this.resolveForceUpdate = resolve;
			});
			// The Promise will resolve ONLY when `resolveForceUpdateAction()` is called
		},
		// Called by the modal when the user acts
		async resolveForceUpdateAction({ skip_offline_sync = false } = {}) {
			if (this.resolveForceUpdate) {
				if (this.force_update_show_details.is_offline_sync != true) {
					// in online sync call the show store function to save the show
					await this.saveShow({ force_save: true, save_triggered_from: SAVE_TRIGGERED_FROM.FORCE_SAVE });
					this.resolveForceUpdate({ was_sync_successful: true }); // not required to pass was_sync_successful here as it's only required for OfflineMode, but keeping it for uniformity.
					this.resolveForceUpdate = undefined;
				} else {
					// first check if the user wants to skip the offline sync
					if (skip_offline_sync == true) {
						// check if the same tab was opened when network got disconnected and after few seconds the network got reStored, the offline sync will be triggered again.
						// as a result conflict COULD be detected, so we need to check if the show_ID matches, if matches and user choose to skip the sync, then we need to refresh the page to fetch latest updates from the server
						let callback_post_sync = undefined;
						if (this.active_show != undefined && this.force_update_show_details.show_ID == this.active_show.show_ID) {
							callback_post_sync = () => {
								window.location.reload();
							};
						}

						// Return skip status
						this.resolveForceUpdate({ skipped: true, callback_post_sync, was_sync_successful: true });
					} else {
						let was_sync_successful = false;
						// in offline sync there can be multiple edge cases
						// edge case 1. The offline sync show + active_show areis same
						// check if the show_ID matches
						if (this.active_show != undefined && this.force_update_show_details.show_ID == this.active_show.show_ID) {
							try {
								const response = await this.saveShow({ force_save: true, save_triggered_from: SAVE_TRIGGERED_FROM.OFFLINE_SYNC_FORCE_SAVE });
								if (response?.error != undefined) {
									was_sync_successful = false;
								} else {
									was_sync_successful = true;
								}
							} catch (error) {
								console.log('error in saveShow edge case 1', error);
								was_sync_successful = false;
							}
						} else {
							// edge case 2. The offline sync show + active_show are different

							// some time this will also be called on refresh when active_show is not set yet, and offline sync is triggered before that. so since active_show will be undefined this edge case will handle the overwrite.
							try {
								const response = await this.saveShow({ force_save: true, non_active_show: this.force_update_show_details, save_triggered_from: SAVE_TRIGGERED_FROM.OFFLINE_SYNC_FORCE_SAVE });
								if (response?.error != undefined) {
									was_sync_successful = false;
								} else {
									was_sync_successful = true;
								}
							} catch (error) {
								console.log('error in saveShow edge case 2', error);
								was_sync_successful = false;
							}
						}

						this.resolveForceUpdate({ was_sync_successful });
					}

					this.resolveForceUpdate = undefined;
				}
			}
			this.force_update_show_details = undefined; // Close modal
		},
	},
	watch: {
		a_change_has_been_made: function (new_value, old_value) {
			if (new_value == old_value) {
				return;
			}

			if (new_value == true) {
				const setting_store = useSettingStore();
				setting_store.elevate_studio_user_actions_broadcast_channel.postMessage({ title: 'CSC_user_saved_edits_to_show', show_id: this.active_show.show_ID });
			}
		},
	},
});
